import { consumirSegundoFactorAtendido, obtenerToken, pedirSegundoFactor } from '../autenticacion'
import { MENSAJE_SIN_CONEXION, mensajeDeErrorDeAutenticacion } from '../erroresDeAutenticacion'

const baseUrl = import.meta.env.VITE_API_URL ?? ''

export class ErrorDeApi extends Error {
  constructor(
    public readonly estado: number,
    mensaje: string,
    public readonly codigo?: string,
    /**
     * El cuerpo JSON del error, cuando es un objeto, para el caso raro en que la pantalla necesita algo
     * más que el mensaje y el código: el `clienteId` de CLIENTE_ARCHIVADO, con el que el alta
     * ofrece reactivar al cliente ahí mismo.
     */
    public readonly cuerpo?: Record<string, unknown>,
  ) {
    super(mensaje)
  }
}

/**
 * Los dos puntos por los que un mensaje en inglés llegaba a la pantalla del contador: MSAL
 * (que no habla español y no es configurable) y el fallo de red de fetch, que el navegador
 * reporta como "Failed to fetch" o "Load failed" y sin más detalle, a propósito.
 *
 * Se normalizan a ErrorDeApi para que las pantallas no tengan que enterarse: todas ya hacen
 * `e instanceof ErrorDeApi ? e.message : <respaldo>`, así que con esto muestran el texto en
 * español sin tocar ninguna de ellas.
 *
 * Estado 0 porque no hubo respuesta HTTP. Ninguna pantalla lo confunde: las que ramifican por
 * estado comparan contra códigos reales (404, 403), nunca contra 0.
 */
export async function tokenDeSesion(obtener: () => Promise<string>): Promise<string> {
  try {
    return await obtener()
  } catch (e) {
    throw new ErrorDeApi(0, mensajeDeErrorDeAutenticacion(e), 'AUTENTICACION')
  }
}

export async function fetchDeApi(url: string, init: RequestInit = {}): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch {
    // fetch solo rechaza por red o CORS. El navegador oculta cuál de los dos fue (es una
    // decisión de seguridad suya), así que el mensaje no puede ser más específico.
    throw new ErrorDeApi(0, MENSAJE_SIN_CONEXION, 'SIN_CONEXION')
  }
}

/**
 * RNF-05 / I-04: el rol del usuario exige MFA y el token todavía no lo trae satisfecho. Pide
 * el segundo factor (redirige la página) en vez de solo mostrar el error — sin esto, el
 * usuario vería "MFA_REQUERIDO" sin ninguna forma de resolverlo desde la SPA.
 *
 * Guard contra llamadas concurrentes: la página suele disparar varias peticiones en paralelo
 * (tablero, notificaciones...), así que sin esto cada una redirige por su cuenta y Entra
 * corta el flujo con AADSTS50196 (LoopDetected) al ver varias /authorize casi simultáneas.
 */
let redirigiendoAMfa = false

/**
 * M3 · Quién le explica al usuario que va a salir de ContaQuick, antes de sacarlo.
 *
 * Hasta ahora el redirect salía de aquí sin avisar: la aplicación se desvanecía a media carga
 * y reaparecía en un dominio de Microsoft. Visto desde fuera es indistinguible de un secuestro
 * de sesión, y el usuario no tenía forma de saber que fue su propio rol el que lo pidió.
 *
 * SI NADIE ESCUCHA, SE REDIRIGE COMO SIEMPRE. Es deliberado: este módulo no puede quedarse
 * esperando un clic que quizá nunca llegue —en una pantalla sin React montado el usuario se
 * quedaría con errores para siempre y sin manera de resolverlos—. La explicación es una
 * mejora del camino normal, no un requisito para que el flujo funcione.
 */
let pedirConfirmacionDeMfa: (() => void) | null = null

export function alPedirSegundoFactor(escucha: (() => void) | null): void {
  pedirConfirmacionDeMfa = escucha
}

/** Lo llama la SPA cuando el usuario pulsa «Continuar» en el diálogo. */
export function continuarConSegundoFactor(): Promise<void> {
  return pedirSegundoFactor()
}

/**
 * El usuario cerró el diálogo sin continuar. Se suelta el guard para que la siguiente
 * respuesta con MFA_REQUERIDO vuelva a preguntarle: sin esto quedaría con la aplicación
 * inservible y sin nada que pulsar, que es peor que el problema original.
 */
export function cancelarSegundoFactor(): void {
  redirigiendoAMfa = false
}

async function manejarSiFaltaMfa(codigo: string | undefined): Promise<void> {
  if (codigo !== 'MFA_REQUERIDO' || redirigiendoAMfa) return
  redirigiendoAMfa = true

  if (pedirConfirmacionDeMfa) {
    // El redirect lo dispara el usuario desde el diálogo. El guard queda puesto para que las
    // peticiones que fallan en paralelo no abran un diálogo cada una.
    pedirConfirmacionDeMfa()
    return
  }

  await pedirSegundoFactor()
}

/**
 * M2 · A quién avisarle de que el segundo factor quedó atendido. Lo registra la SPA al montar
 * (ver App.tsx); aquí vive el gancho porque el único sitio que puede PROBARLO es este: una
 * respuesta aceptada por el servidor después de la elevación. Volver del redirect no prueba
 * nada —el usuario pudo cancelar en la pantalla de Microsoft—; que el API deje de contestar
 * MFA_REQUERIDO, sí.
 */
let avisarDeSegundoFactor: (() => void) | null = null

export function alAtenderSegundoFactor(escucha: (() => void) | null): void {
  avisarDeSegundoFactor = escucha
}

/** Se llama tras CADA respuesta aceptada; la marca es de un solo uso, así que avisa una vez. */
function confirmarSegundoFactorSiVeniaDeUno(): void {
  if (consumirSegundoFactorAtendido()) avisarDeSegundoFactor?.()
}

/**
 * Cliente HTTP de la API. Adjunta el token en cada llamada.
 * El despacho (tenant) NO viaja en la petición: el servidor lo deriva del token (RF-102).
 */
async function peticion<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const token = await tokenDeSesion(obtenerToken)

  const respuesta = await fetchDeApi(`${baseUrl}${ruta}`, {
    ...opciones,
    headers: {
      // Con FormData el Content-Type lo pone el navegador: lleva el boundary del multipart, y
      // fijarlo a mano (o dejar el de JSON) hace que el API no encuentre el archivo. Igual que
      // en apiPlataforma.ts.
      ...(opciones.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      Authorization: `Bearer ${token}`,
      ...opciones.headers,
    },
  })

  if (!respuesta.ok) {
    let mensaje = `Error ${respuesta.status}`
    let codigo: string | undefined
    let cuerpo: Record<string, unknown> | undefined

    try {
      const json: unknown = await respuesta.json()
      cuerpo = json !== null && typeof json === 'object' ? (json as Record<string, unknown>) : undefined
      mensaje = (cuerpo?.error as string | undefined) ?? mensaje
      codigo = cuerpo?.codigo as string | undefined
    } catch {
      /* la respuesta no traía JSON */
    }

    await manejarSiFaltaMfa(codigo)
    throw new ErrorDeApi(respuesta.status, mensaje, codigo, cuerpo)
  }

  confirmarSegundoFactorSiVeniaDeUno()

  return respuesta.status === 204 ? (undefined as T) : ((await respuesta.json()) as T)
}

/** Descarga un archivo autenticado y hace que el navegador lo guarde (RF-705). */
/**
 * Descarga un archivo y devuelve las cabeceras de la respuesta: algunas descargas traen
 * metadatos que la pantalla necesita —si el Anexo 24 se regeneró con contenido distinto al
 * presentado, por ejemplo— y no caben en el cuerpo, que es el archivo.
 */
async function descargarArchivo(ruta: string, nombrePorDefecto: string, opciones: RequestInit = {}): Promise<Headers> {
  const token = await tokenDeSesion(obtenerToken)
  const respuesta = await fetchDeApi(`${baseUrl}${ruta}`, {
    ...opciones,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(opciones.body ? { 'Content-Type': 'application/json' } : {}),
      ...opciones.headers,
    },
  })

  if (!respuesta.ok) {
    const cuerpoError = await respuesta.json().catch(() => ({}))
    await manejarSiFaltaMfa(cuerpoError.codigo)
    throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
  }

  const nombre =
    respuesta.headers.get('Content-Disposition')?.match(/filename="?([^\";]+)"?/)?.[1] ?? nombrePorDefecto
  const enlace = document.createElement('a')
  enlace.href = URL.createObjectURL(await respuesta.blob())
  enlace.download = nombre
  enlace.click()
  URL.revokeObjectURL(enlace.href)
  return respuesta.headers
}

/** Arma la query string de PL/XF/XC: numOrden (AF/FC) o numTramite (DE/CO) según el tipo de solicitud (RF-803). */
function parametrosSolicitud(
  anio: number,
  mes: number,
  tipoSolicitud: string,
  numOrden?: string,
  numTramite?: string,
): string {
  const parametros = new URLSearchParams({ anio: String(anio), mes: String(mes), tipoSolicitud })
  if (numOrden) parametros.set('numOrden', numOrden)
  if (numTramite) parametros.set('numTramite', numTramite)
  return parametros.toString()
}

/**
 * Filtros de la cartera como query string (RF-205, RF-705). Los arma una sola función a
 * propósito: el listado y la exportación tienen que pedir exactamente el mismo recorte —el
 * backend también los comparte en un único filtro— o el archivo dejaría de ser lo que el
 * contador ve en pantalla.
 */
function parametrosDeCartera(filtros: FiltrosDeCartera): URLSearchParams {
  const parametros = new URLSearchParams()
  if (filtros.busqueda) parametros.set('busqueda', filtros.busqueda)
  if (filtros.tipoPersona) parametros.set('tipoPersona', filtros.tipoPersona)
  if (filtros.estado) parametros.set('estado', filtros.estado)
  if (filtros.regimenFiscal) parametros.set('regimenFiscal', filtros.regimenFiscal)
  return parametros
}

// ---------------------------------------------------------------- Tipos

export type TipoPersona = 'Fisica' | 'Moral'

export type RolDeUsuario = 'Administrador' | 'Contador' | 'Auxiliar' | 'SoloLectura'

/** RF-1707 · Qué ayuda ya vio u omitió el usuario en curso. Vive en el servidor y lo sigue entre equipos. */
export interface EstadoDeAyuda {
  /** Recorrido → versión vista u omitida (RN-66: si la versión del recorrido sube, se ofrece otra vez). */
  recorridos: Record<string, number>
  tips: string[]
  novedades: string[]
  mostrarTips: boolean
  /** Fecha local (yyyy-MM-dd) en que cerró el TIP del día; null si nunca. */
  tipDelDiaCerradoEl: string | null
  /** Alta del usuario: una novedad anterior no se le muestra (RF-1706). */
  usuarioDesdeUtc: string
}

export type TipoDeAyuda = 'Recorrido' | 'Tip' | 'Novedad' | 'TipDelDia'

export interface Despacho {
  id: string
  nombreComercial: string
  plan: string
  estado: string
  maxClientes: number
  maxUsuarios: number
  clientesActivos: number
  usuariosActivos: number
  /**
   * Rol de quien pregunta, no del despacho. Viaja aquí porque la SPA ya pide este endpoint al
   * arrancar; sin él la navegación ofrece secciones que el API rechaza con 403 (RF-104).
   * Es una pista para la interfaz: quien manda sigue siendo el servidor.
   */
  rolActual: RolDeUsuario
}

export type SemaforoDeCumplimiento = 'AlCorriente' | 'PorVencer' | 'Vencido'

// ---- Conciliación bancaria (RF-1300 a RF-1303) ----

/** Una institución del catálogo c_Banco del SAT. */
export interface BancoSat {
  clave: string
  nombre: string
}

/**
 * Cuenta de cheques o depósito del cliente (RF-1300). Solo los últimos cuatro dígitos (RN-41) y
 * ligada a una cuenta contable de registro que no comparte con otra cuenta bancaria (RN-40).
 */
export interface CuentaBancaria {
  id: string
  claveBanco: string
  banco: string
  moneda: string
  ultimosCuatroDigitos: string
  alias: string | null
  /** Saldo al INICIO del día de `fechaDelSaldoInicial`. */
  saldoInicial: number
  fechaDelSaldoInicial: string
  estado: 'Activa' | 'Desactivada'
  cuentaContableId: string
  cuentaContable: string
  movimientos: number
  ultimoMovimiento: string | null
}

export interface AltaDeCuentaBancaria {
  cuentaContableId: string
  claveBanco: string
  moneda: string
  ultimosCuatroDigitos: string
  alias?: string
  saldoInicial: number
  fechaDelSaldoInicial: string
}

export type CampoBancario = 'Fecha' | 'Concepto' | 'Referencia' | 'Cargo' | 'Abono' | 'ImporteConSigno' | 'Saldo'

/** El campo canónico y su columna: el título del encabezado o la posición como «#3». */
export interface ColumnaDePerfil {
  campo: CampoBancario
  columna: string
}

/** Cómo se lee el estado de cuenta de un banco (RF-1301). Es del despacho y se reusa entre clientes. */
export interface PerfilBancario {
  id: string
  nombre: string
  /** null: se detecta en el encabezado. */
  separador: string | null
  /** null: la primera hoja del XLSX. */
  hoja: string | null
  /** null: el primer renglón que trae todos los títulos mapeados. */
  renglonDeEncabezado: number | null
  formatoDeFecha: string
  separadorDecimal: string
  positivoEsAbono: boolean
  columnas: ColumnaDePerfil[]
  creadoEnUtc: string
  modificadoEnUtc: string | null
}

export interface DatosDePerfilBancario {
  nombre: string
  separador?: string
  hoja?: string
  renglonDeEncabezado?: number
  formatoDeFecha: string
  separadorDecimal: string
  positivoEsAbono: boolean
  columnas: ColumnaDePerfil[]
}

export interface RenglonDeEstadoDeCuenta {
  renglon: number
  fecha: string
  concepto: string
  referencia: string | null
  cargo: number
  abono: number
  saldo: number | null
}

export interface RenglonRechazado {
  renglon: number
  motivo: string
}

/**
 * Lo que dice el estado de cuenta (RF-1302): los conteos son totales; las muestras traen hasta 500
 * renglones cada una. En la vista previa `importacionId` es null porque no se escribió nada.
 */
export interface ResultadoDeEstadoDeCuenta {
  renglonesDeDatos: number
  nuevos: number
  duplicados: number
  rechazados: number
  muestraDeNuevos: RenglonDeEstadoDeCuenta[]
  muestraDeDuplicados: RenglonDeEstadoDeCuenta[]
  muestraDeRechazados: RenglonRechazado[]
  importacionId: string | null
}

export interface ImportacionBancaria {
  id: string
  nombreDeArchivo: string
  perfil: string
  renglones: number
  incorporados: number
  duplicados: number
  rechazados: number
  importadaEnUtc: string
}

export interface MovimientoBancario {
  id: string
  fecha: string
  concepto: string
  referencia: string | null
  cargo: number
  abono: number
  saldo: number | null
  estadoDeConciliacion: 'Pendiente' | 'Conciliado'
  numeroDeRenglon: number
  importacionId: string
}

// ---- Conciliación bancaria · emparejamiento (RF-1304, RF-1305, RF-1307) ----
//
// Todos los importes vienen CON SIGNO y alineados: positivo es dinero que entró al banco (un
// depósito, que en la póliza es un cargo a la cuenta de bancos) y negativo el que salió. Un
// emparejamiento cuadra cuando la suma del lado bancario es idéntica a la del otro lado (RN-37).

/** Un movimiento bancario todavía sin emparejar. */
export interface MovimientoPendiente {
  id: string
  fecha: string
  concepto: string
  referencia: string | null
  importe: number
}

/** Un movimiento de póliza sobre la cuenta contable ligada a la cuenta bancaria. */
export interface MovimientoContable {
  id: string
  polizaId: string
  tipoPoliza: TipoPoliza
  folio: number
  fecha: string
  concepto: string
  referencia: string | null
  importe: number
}

/** Una factura que el pago salda, según el complemento de pago del CFDI. */
export interface FacturaSaldada {
  uuid: string
  serie: string | null
  folio: string | null
  importePagado: number | null
}

/** Un pago de un CFDI tipo P del cliente (RF-1307): el comprobante y el número de pago dentro de él. */
export interface PagoCfdi {
  comprobanteId: string
  indiceDePago: number
  uuid: string
  fechaPago: string
  importe: number
  /** RFC y nombre de la otra parte del pago. */
  contraparte: string
  facturas: FacturaSaldada[]
}

export interface PartidasPendientes {
  bancarios: MovimientoPendiente[]
  contables: MovimientoContable[]
  pagos: PagoCfdi[]
}

/**
 * Lo que propone el motor para un movimiento bancario (RF-1304, RF-1307). No se guarda: se recalcula
 * y no concilia nada hasta que el contador la confirma (RN-36).
 */
export interface PropuestaDeEmparejamiento {
  movimientoBancario: MovimientoPendiente
  tipo: 'Poliza' | 'PagoCfdi'
  contable: MovimientoContable | null
  pago: PagoCfdi | null
  /** De 0 a 100. */
  confianza: number
  motivos: string[]
}

export type OrigenDeEmparejamiento = 'Automatico' | 'Manual'

export interface ReferenciaDePago {
  comprobanteId: string
  indiceDePago: number
}

/** Una partida a emparejar: bancarios contra pólizas O contra pagos de CFDI, nunca mezclados. */
export interface PartidaDeEmparejamiento {
  movimientosBancarios: string[]
  movimientosContables: string[]
  pagos: ReferenciaDePago[]
  origen: OrigenDeEmparejamiento
  confianza: number | null
}

export interface EmparejamientoBancario {
  id: string
  origen: OrigenDeEmparejamiento
  confianza: number | null
  vigente: boolean
  importe: number
  creadoEnUtc: string
  deshechoEnUtc: string | null
  bancarios: MovimientoPendiente[]
  contables: MovimientoContable[]
  pagos: PagoCfdi[]
}

export interface PeriodoBancario {
  desde?: string
  hasta?: string
}

// ---- Conciliación bancaria · el documento con cierre (RF-1308) y la antigüedad (RF-1309) ----

export type LadoDePartidaEnConciliacion = 'Banco' | 'Poliza'
export type EstadoDeConciliacionDelPeriodo = 'Abierta' | 'Cerrada'

/**
 * Una partida en conciliación: un movimiento del banco sin reflejo en la contabilidad o uno de
 * póliza sin reflejo en el banco. El importe trae el signo alineado al banco (positivo = entró
 * dinero) y `dias` es su antigüedad a la fecha de corte.
 */
export interface PartidaEnConciliacion {
  lado: LadoDePartidaEnConciliacion
  id: string
  fecha: string
  concepto: string
  referencia: string | null
  importe: number
  dias: number
  /** Por qué sigue en conciliación cuando no es obvio, p. ej. «Emparejado con un CFDI de pago, sin póliza». */
  nota: string | null
  polizaId: string | null
  tipoPoliza: string | null
  folio: number | null
}

/**
 * La conciliación bancaria de una cuenta y un mes como documento (RF-1308). Mientras está abierta
 * se calcula al momento; cerrada, es la foto del cierre y no cambia (RN-38). El servidor dice si se
 * puede cerrar y, si no, por qué (RN-39).
 */
export interface ConciliacionDelPeriodo {
  anio: number
  mes: number
  estado: EstadoDeConciliacionDelPeriodo
  fechaDeCorte: string
  saldoSegunBanco: number
  saldoContable: number
  /** saldoSegunBanco − saldoContable. */
  diferencia: number
  /** El saldo inicial del banco contra el contable a esa fecha; distinta de cero, no la explica ninguna partida. */
  diferenciaInicial: number
  partidasBancarias: PartidaEnConciliacion[]
  partidasContables: PartidaEnConciliacion[]
  sumaPartidasBancarias: number
  sumaPartidasContables: number
  sinExplicar: number
  puedeCerrar: boolean
  motivoParaNoCerrar: string | null
  avisos: string[]
  cerradaEnUtc: string | null
  reabiertaEnUtc: string | null
  motivoDeReapertura: string | null
}

export interface TramoDeAntiguedad {
  etiqueta: string
  desdeDias: number
  hastaDias: number | null
  cantidad: number
  importe: number
  semaforo: SemaforoDeCumplimiento
}

export interface PartidaConAntiguedad extends PartidaEnConciliacion {
  tramo: string
  semaforo: SemaforoDeCumplimiento
}

/** RF-1309: las partidas en conciliación de la cuenta a una fecha de corte, por tramos de antigüedad. */
export interface PartidasEnConciliacion {
  corte: string
  tramos: TramoDeAntiguedad[]
  partidas: PartidaConAntiguedad[]
}

export interface ProximaObligacion {
  anio: number
  mes: number
  fechaLimite: string
  yaCumplida: boolean
  semaforo: SemaforoDeCumplimiento
}

/**
 * El pago provisional del mes que toca atender (RF-1209). Desde RF-1606, «cumplido» es DECLARACIÓN
 * PRESENTADA —registrada en la pantalla de declaraciones—, ya no papel sellado. Los dos pasos viajan
 * por separado para que la pantalla diga cuál falta: sellar el papel o registrar la presentación.
 */
export interface ProximoPagoProvisional {
  anio: number
  mes: number
  fechaLimite: string
  /** De dónde sale la fecha: el día 17 y los días del sexto dígito del RFC, o por qué no los hay. */
  origenDelPlazo: string
  papelDeIvaSellado: boolean
  /** Nulo cuando ContaQuick no calcula el ISR de este cliente (solo lo calcula a morales 601). */
  papelDeIsrSellado: boolean | null
  /** RF-1606 · Hay presentación vigente de IVA registrada. */
  ivaPresentada: boolean
  /** RF-1606 · Nulo cuando ContaQuick no calcula el ISR de este cliente: no es ni sí ni no. */
  isrPresentada: boolean | null
  /** Declaración presentada: IVA y, si ContaQuick calcula el ISR del cliente, también ISR. */
  yaCumplido: boolean
  semaforo: SemaforoDeCumplimiento
}

// ---- RF-1605 · Control de presentación de declaraciones (ISR, IVA y DIOT) ----

export type TipoDeDeclaracion = 'IVA' | 'ISR' | 'DIOT'
export type CaracterDeDeclaracion = 'Normal' | 'Complementaria'
export type EstadoDePresentacion = 'Presentada' | 'Acusada'
/** Etapa de una declaración en el periodo. PorGenerar: no hay papel sellado (o ContaQuick no lo genera: ver `explicacion`). */
export type EtapaDeDeclaracion = 'PorGenerar' | 'Generada' | 'Presentada' | 'Acusada'

export interface ResumenDePapel {
  id: string
  generadoEnUtc: string
  impuestoAPagar: number
  saldoAFavorDelPeriodo: number
}

/**
 * Una presentación registrada (RF-1605). Registrarla NO presenta nada ante el SAT (RN-62): el
 * contador presenta en el portal con la e.firma del contribuyente y aquí deja la evidencia.
 */
export interface PresentacionDeDeclaracion {
  id: string
  anio: number
  mes: number
  declaracion: TipoDeDeclaracion
  caracter: CaracterDeDeclaracion
  estado: EstadoDePresentacion
  fechaDePresentacion: string
  numeroDeOperacion: string
  /** El papel sellado que se presentó; nulo en la DIOT o cuando no había papel. */
  papelDeTrabajoId: string | null
  registradaEnUtc: string
  /** Nulo: todavía no hay acuse resguardado. */
  nombreDelAcuse: string | null
  acuseResguardadoEnUtc: string | null
  /** Falso: anulada. Se sigue mostrando como rastro. */
  vigente: boolean
  anuladaEnUtc: string | null
  motivoDeAnulacion: string | null
  /** Verdadero si se presentó después de la fecha límite; nulo cuando no hay plazo que comparar (DIOT). */
  despuesDelPlazo: boolean | null
}

export interface SeguimientoDeDeclaracion {
  declaracion: TipoDeDeclaracion
  etapa: EtapaDeDeclaracion
  /** Por qué está en esa etapa cuando no es obvio. Nulo si no hace falta. */
  explicacion: string | null
  /** Fecha límite del pago provisional (día 17 + sexto dígito). Nula en la DIOT. */
  fechaLimite: string | null
  origenDelPlazo: string | null
  papelVigente: ResumenDePapel | null
  /** El papel ligado a la presentación vigente más reciente. */
  papelPresentado: ResumenDePapel | null
  /** Se selló un papel DESPUÉS del que se presentó: puede proceder una complementaria. */
  papelMasRecienteQueElPresentado: boolean
  /** Todas, la más reciente primero, incluidas las anuladas. */
  presentaciones: PresentacionDeDeclaracion[]
}

export interface SeguimientoDeDeclaraciones {
  anio: number
  mes: number
  /** Siempre en este orden: IVA, ISR, DIOT. */
  declaraciones: SeguimientoDeDeclaracion[]
}

export interface RegistrarPresentacionRequest {
  anio: number
  mes: number
  declaracion: TipoDeDeclaracion
  caracter: CaracterDeDeclaracion
  fechaDePresentacion: string
  numeroDeOperacion: string
  /** Si se omite, el servidor liga el papel vigente (IVA/ISR). Prohibido en la DIOT. */
  papelDeTrabajoId?: string | null
}

export type TipoNotificacion =
  | 'VencimientoDeObligacion'
  | 'DescargaMasivaTerminada'
  | 'ErrorDeServicioSat'
  | 'CertificadoPorVencer'
  | 'CancelacionDeCfdiRelevante'
  /** RF-1609 · Un envío o una declaración presentada lleva días sin acuse. Solo en la campana, nunca al cliente. */
  | 'AcusePendiente'

export interface Notificacion {
  id: string
  clienteId: string | null
  clienteRazonSocial: string | null
  tipo: TipoNotificacion
  titulo: string
  mensaje: string
  creadaEnUtc: string
  leidaEnUtc: string | null
  correoEnviadoEnUtc: string | null
}

/** Versión vigente del aviso de privacidad y si el usuario en sesión ya la aceptó (LFPDPPP, P-102). */
export interface AvisoDePrivacidad {
  version: string
  aceptado: boolean
}

export interface ResumenDeCliente {
  clienteId: string
  rfc: string
  razonSocial: string
  /** Los plazos de la contabilidad electrónica difieren entre PM y PF: el tablero los separa. */
  tipoPersona: TipoPersona
  periodosAbiertos: number
  cfdiSinContabilizar: number
  proximaObligacion: ProximaObligacion
  enviosPendientes: number
  enviosRechazados: number
  semaforo: SemaforoDeCumplimiento
  /** P-99 (RF-802/RF-806, G-04/G-09): ejercicio y fecha límite de la próxima balanza ajustada (periodo 13). */
  anioDelEjercicioBalanzaAjustada: number
  limiteBalanzaAjustada: string
  /** RF-901: si esa balanza ajustada ya se envió o aceptó. */
  ajusteDeCierreCumplido: boolean
  /** RF-1209: el pago provisional del mes; su semáforo ya va fundido en `semaforo`. */
  pagoProvisional: ProximoPagoProvisional
  /** RF-507: cuántos proveedores distintos del cliente aparecen en el listado 69-B; cero es «cartera limpia». */
  proveedoresEnRiesgo: number
  /**
   * RF-1309: movimientos del estado de cuenta de este cliente sin emparejar con más de 90 días.
   * No entra al semáforo del cliente (ese es de la balanza): el tablero lo muestra aparte.
   */
  movimientosBancariosAntiguos: number
}

export interface Cliente {
  id: string
  tipoPersona: TipoPersona
  rfc: string
  razonSocial: string
  regimenFiscal: string
  regimenDescripcion?: string
  codigoPostalFiscal: string
  /**
   * Nulo cuando no se capturó en el alta: el campo siempre viaja, el dato no siempre existe.
   * Quien lo pinte debe resolver ese caso en vez de dejar el renglón en blanco (RF-201).
   */
  correoContacto?: string | null
  estado: string
  fechaInicioOperaciones: string
  /**
   * RF-1209: sus pagos provisionales vencen el día 17, sin el plazo escalonado por sexto dígito del
   * RFC (art. 5.1). El servidor siempre lo manda; ausente se lee como «no».
   */
  excluidoDelPlazoEscalonado?: boolean
}

/**
 * El recorte de la cartera: lo que se ve en pantalla y, con los mismos valores, lo que se
 * lleva el archivo exportado (RF-205, RF-705). El RFC y la razón social se buscan juntos con
 * `busqueda`; el API no distingue entre uno y otra.
 */
export interface FiltrosDeCartera {
  busqueda?: string
  tipoPersona?: TipoPersona
  estado?: 'Activo' | 'Archivado'
  regimenFiscal?: string
}

/**
 * Los datos fiscales que sí se editan (RF-201). No lleva RFC ni tipo de persona: cambiar
 * cualquiera de los dos es dar de alta a otro contribuyente, y el API los rechaza en
 * silencio porque ni siquiera los lee del cuerpo.
 *
 * Es un PUT, o sea el reemplazo completo de los datos fiscales: lo que no se manda no se
 * conserva. Para no perder el correo guardado hay que reenviarlo.
 */
export interface EdicionDeCliente {
  razonSocial: string
  regimenFiscal: string
  codigoPostalFiscal: string
  /** Fecha suelta, sin hora ni zona: 'AAAA-MM-DD', como la devuelve el API. */
  fechaInicioOperaciones: string
  /**
   * Nulo u omitido BORRA el correo guardado. Ojo con el campo vacío de un formulario: la
   * cadena '' se guarda tal cual, no borra. Si el contador dejó el campo en blanco, manda
   * null (o `correo.trim() || null`), nunca ''.
   */
  correoContacto?: string | null
  /** RF-1209: omitido es «no», como el resto de un PUT. */
  excluidoDelPlazoEscalonado?: boolean
}

export interface RegimenFiscal {
  clave: string
  descripcion: string
  aplicaFisica: boolean
  aplicaMoral: boolean
}

export interface Usuario {
  id: string
  nombre: string
  correo: string
  rol: string
  estado: string
  mfaHabilitado: boolean
  ultimoAccesoUtc?: string
  /**
   * U2 · Cuántos clientes atiende. **Nulo para el Administrador**, y no cero: un administrador
   * no tiene filas de asignación porque ve toda la cartera (I-02, A-05), así que un cero diría
   * «no atiende a nadie» de quien los atiende a todos.
   */
  clientesAsignados: number | null
  creadoEnUtc: string
}

/**
 * #149 · Qué pasó con la cuenta del CIAM de quien se invita. `Error` es que Microsoft Graph
 * falló: la invitación quedó, la cuenta no, y el correo no salió porque diría «entra» a quien
 * no puede entrar.
 */
export type EstadoDeCuentaDeAcceso = 'Creada' | 'YaExistia' | 'Reactivada' | 'NoConfigurado' | 'Error'

/** Lo que devuelven la invitación, su reenvío y el alta de un despacho (#149). */
export interface ResultadoDeInvitacion {
  correoEnviado: boolean
  cuenta: EstadoDeCuentaDeAcceso
  /** Lo que quien invita tiene que hacer a mano para que la persona pueda entrar; null si nada. */
  problemaDeCuenta: string | null
}

/** RF-107 (§6.4): consentimiento del despacho para que soporte de plataforma vea su contenido contable. */
export interface ConsentimientoDeSoporte {
  id: string
  proposito: string
  creadoEnUtc: string
  vigenteHastaUtc: string
  revocadoEnUtc?: string
  activo: boolean
}

/** Resultado de descargar y sincronizar el listado 69-B desde el SAT bajo demanda (A-06). */
export interface ResumenDeSincronizacionDesdeElSat {
  exitosas: number
  nuevos: number
  actualizados: number
  conError: number
  fallidas: { url: string; mensaje: string }[]
}

export interface RegistroBitacora {
  id: string
  fechaHoraUtc: string
  usuarioId?: string
  /**
   * Nombre de quien hizo la acción (B1). Nulo en los asientos del sistema —los que el dominio
   * marca como «sin atribuirse a ningún usuario»—: no es un dato que falte, es otro actor.
   * Acompaña a `usuarioId`, no lo sustituye: el id sigue sirviendo para filtrar y para cruzar
   * registros aunque el usuario se renombre después.
   */
  usuarioNombre?: string | null
  entidadAfectada: string
  accion: string
  idRegistro: string
  direccionIp?: string
  detalle: string
}


export interface EstadoCredencial {
  credencialId: string
  clienteId: string
  clienteRfc: string
  clienteRazonSocial: string
  vigenciaInicioUtc: string
  vigenciaFinUtc: string
  diasParaVencer: number
  vigente: boolean
  revocada: boolean
  consentimientoProposito: string
  consentimientoFechaUtc: string
}

export interface ResultadoDeCarga {
  credencialId: string
  rfc: string
  titularNombre: string
  vigenciaFinUtc: string
  diasParaVencer: number
  advertencia?: string
}

/**
 * Estado del sello del contribuyente ante el proveedor de timbrado (RF-1001, RF-1002; ADR-013).
 * Los cuatro estados existen para que la custodia de aquí y el sello de allá no puedan diferir
 * en silencio; solo 'Aprovisionado' permite emitir.
 */
export type EstadoDelSelloRemoto =
  | 'SinSello'
  | 'Aprovisionado'
  | 'AprovisionamientoPendiente'
  | 'RetiroPendiente'

export interface EstadoDelSelloDigital {
  clienteId: string
  clienteRfc: string
  credencial: EstadoCredencial | null
  proveedor: string | null
  estadoRemoto: EstadoDelSelloRemoto | null
  numeroDeSerieDelSello: string | null
  vigenciaDelSelloFinUtc: string | null
  puedeEmitir: boolean
  ultimoError: string | null
  ultimoErrorEnUtc: string | null
}

/** Un catálogo o está cargado o no; no hay término medio que reportar (RF-1003). */
export type EstadoDeCatalogo = 'Cargado' | 'NoCargado'

/** Las siete rutas de los catálogos cortos, tal como las publica la API. */
export type RutaDeCatalogo =
  | 'uso-cfdi'
  | 'formas-pago'
  | 'metodos-pago'
  | 'objeto-impuesto'
  | 'tipos-relacion'
  | 'motivos-cancelacion'
  | 'monedas'

export interface ClaveDeCatalogo {
  clave: string
  descripcion: string
  aplicaFisica?: boolean | null
  aplicaMoral?: boolean | null
  decimales?: number | null
}

export interface CatalogoCortoDelSat {
  catalogo: string
  estado: EstadoDeCatalogo
  version: string | null
  publicadoPorSat: string | null
  actualizadoEnUtc: string | null
  cotejado: boolean
  /** Incompleto a propósito (c_Moneda, c_ObjetoImp): puede faltar una clave que sí existe. */
  parcial: boolean
  total: number
  items: ClaveDeCatalogo[]
}

export interface ClaveProdServ {
  clave: string
  descripcion: string
  incluyeIva?: number | null
  incluyeIeps?: number | null
  complemento?: string | null
}

export interface ClaveUnidad {
  clave: string
  nombre: string
  simbolo?: string | null
}

export interface PaginaDeClaves<T> {
  catalogo: string
  estado: EstadoDeCatalogo
  total: number
  pagina: number
  tamano: number
  items: T[]
}

/** Estado, procedencia y última carga de un catálogo. Es lo que se le muestra al contador. */
export interface ResumenDeCatalogo {
  catalogo: string
  estado: EstadoDeCatalogo
  clavesVigentes: number
  version: string | null
  publicadoPorSat: string | null
  actualizadoEnUtc: string | null
  cotejado: boolean
  parcial: boolean
  origen: string | null
  ultimaCargaUtc: string | null
  ultimaCargaEstado: string | null
  ultimaCargaError: string | null
}

export interface ResultadoDelSello {
  credencialId: string
  rfc: string
  titularNombre: string
  vigenciaFinUtc: string
  diasParaVencer: number
  proveedor: string
  estadoRemoto: EstadoDelSelloRemoto
  puedeEmitir: boolean
  advertencia?: string
}

/** Extracción asistida de la CSF (RF-202): cada campo puede faltar, nunca se prellena a ciegas. */
export interface ResultadoExtraccionCsf {
  rfc?: string
  regimenFiscal?: string
  codigoPostal?: string
  fragmentoRfc?: string
  fragmentoRegimen?: string
  fragmentoCodigoPostal?: string
  advertencia?: string
}

export interface Pagina<T> {
  total: number
  pagina: number
  items: T[]
}

// ---- Catálogo de cuentas (RF-301..303, RN-05) ----

export type Naturaleza = 'Deudora' | 'Acreedora'

export type Plantilla =
  | 'PersonaMoralRegimenGeneral'
  | 'PersonaFisicaActividadEmpresarial'
  | 'Arrendamiento'
  | 'Resico'

export interface CuentaDeCatalogo {
  id: string
  cuentaPadreId?: string
  numero: string
  nombre: string
  nivel: number
  naturaleza: Naturaleza
  codigoAgrupadorSat?: string
  agrupadorNombre?: string
  esDeRegistro: boolean
  estado: 'Activa' | 'Desactivada'
  /**
   * Movimientos de pólizas vigentes en el periodo consultado (K2). Nulo cuando no se pidió
   * periodo y también en las cuentas acumulativas: ahí un cero se leería como «no tuvo», y lo
   * correcto es «no puede tener». Una póliza cancelada conserva sus movimientos por
   * trazabilidad pero deja de contar.
   */
  movimientosEnPeriodo?: number | null
}

export interface CodigoAgrupador {
  codigo: string
  nombre: string
  nivel: number
  naturaleza: Naturaleza
}

export interface ValidacionCt {
  listoParaCt: boolean
  cuentasSinAgrupador: { id: string; numero: string; nombre: string }[]
}

/** RF-802: tipo de envío de la balanza electrónica (N/C en el XSD oficial). */
export type TipoEnvioBalanza = 'Normal' | 'Complementaria'

/** RF-803: motivo de la solicitud del SAT que origina PL/XF/XC (AF/FC exigen numOrden; DE/CO exigen numTramite). */
export type TipoSolicitudFiscalizacion = 'ActoDeFiscalizacion' | 'FiscalizacionCompulsa' | 'Devolucion' | 'Compensacion'

/** RF-805: tipo de archivo del Anexo 24, tal como aparece en la nomenclatura oficial. */
export type TipoArchivoContabilidadElectronica = 'Ct' | 'Bn' | 'Bc' | 'Pl' | 'Xf' | 'Xc'

/** RF-805: estado de envío de un archivo al Buzón Tributario. */
export type EstadoDeEnvio = 'Generado' | 'Enviado' | 'Aceptado' | 'Rechazado'

/** RF-1603 · Lo que el contador necesita para presentar un archivo concreto. */
export interface GuiaDePresentacion {
  que: string
  donde: string
  conQue: string
  nombreDeArchivo: string | null
  fechaLimite: string | null
  nota: string
  vigenteDesde: string | null
}

export interface ControlDeEnvio {
  id: string
  anio: number
  mes: number
  tipoArchivo: TipoArchivoContabilidadElectronica
  estado: EstadoDeEnvio
  generadoEnUtc: string
  enviadoEnUtc: string | null
  acuseEnUtc: string | null
  folioAcuse: string | null
  selloAcuse: string | null
  motivoRechazo: string | null
  /** El ZIP tal como se nombró: {RFC}{AAAA}{MM}{TIPO}.zip (RN-07, A6). */
  nombreDeArchivo: string | null
  /**
   * Esquemas XSD oficiales contra los que se validó, y cuándo (A7). No existe un caso «no
   * pasó»: la validación revienta antes de producir el ZIP, así que un control solo existe si
   * el archivo era válido. Lo que esto dice es contra qué VERSIÓN se validó.
   */
  esquemasXsd: string | null
  validadoEnUtc: string | null
  /** RF-1604 · Si está resguardado el archivo del acuse, la pantalla ofrece descargarlo. */
  nombreAcuseArchivo: string | null
}

export interface ResultadoEdicionCuenta {
  cuenta: CuentaDeCatalogo
  procedeReenviarCatalogoSat: boolean
}

export interface ResultadoImportacion {
  exito: boolean
  cuentasCreadas: number
  errores: { fila: number; mensaje: string }[]
}

// ---- Descarga masiva ante el SAT (RF-501, RF-502) ----

/** 'Pendiente' (P-95): registrada, todavía sin presentarse al SAT; lo hace el trabajador de la cola. */
export type EstadoSolicitudDescarga = 'Pendiente' | 'Aceptada' | 'EnProceso' | 'Terminada' | 'Error' | 'Rechazada' | 'Vencida'

export interface SolicitudDeDescarga {
  id: string
  loteId: string
  tipo: TipoDescargaCfdi
  fechaInicial: string
  fechaFinal: string
  estado: EstadoSolicitudDescarga
  mensaje?: string
  numeroDeCfdis: number
  paquetesEsperados: number
  paquetesDescargados: number
  intentos: number
  solicitadaEnUtc: string
  ultimaVerificacionUtc?: string
  segundosParaReintentar?: number
}

export interface ErrorDeParticion {
  fechaInicial: string
  fechaFinal: string
  mensaje: string
}

export interface ResultadoDeLote {
  loteId: string
  particiones: SolicitudDeDescarga[]
  errores: ErrorDeParticion[]
}

export interface ResultadoDeCotejo {
  esperadosPorSat: number
  encontradosEnRepositorio: number
  completo: boolean
}

// ---- Estatus de CFDI y listado 69-B (RF-206, RF-505, RF-507) ----

export interface ResumenDeVerificacion {
  verificados: number
  cancelados: number
  sinResultado: number
}

/**
 * Lo que dejó un reproceso de complementos de recepción de pagos (RF-1201).
 *
 * `parcialidadesNuevas` es el número que de verdad importa: es cuánto cobro efectivo estaba
 * invisible para el IVA en base de flujo hasta que se releyó el XML custodiado.
 */
export interface ResumenDeReproceso {
  revisados: number
  conComplemento: number
  parcialidadesNuevas: number
  tiposDeCambioCompletados: number
  conError: number
  errores: string[]
}

/** Un envío del Anexo 24 mirado desde el despacho, con el cliente al lado (A1, RF-805). */
/** RF-1608 · Un archivo del Anexo 24 del periodo generado y sin enviar, o rechazado. */
export interface ArchivoPorPresentar {
  controlId: string
  tipoArchivo: TipoArchivoContabilidadElectronica
  estado: Extract<EstadoDeEnvio, 'Generado' | 'Rechazado'>
  nombreDeArchivo: string | null
}

/** RF-1608 · Qué le falta presentar a un cliente en el periodo. */
export interface PendienteDeCliente {
  clienteId: string
  razonSocial: string
  rfc: string
  /** Archivos del Anexo 24 del periodo generados y sin enviar, o rechazados. */
  archivos: ArchivoPorPresentar[]
  /** Declaraciones con papel sellado y sin presentación vigente. */
  declaraciones: ('IVA' | 'ISR')[]
  /** Lo que todavía no se puede presentar porque falta generarlo, ya redactado (p. ej. «Papel de IVA sin sellar»). */
  pendientesDeGenerar: string[]
  /** Envíos marcados como enviados sin acuse, y declaraciones presentadas sin el archivo del acuse. */
  acusesPendientes: number
  /** Hay archivos o declaraciones: hay paquete que descargar. */
  hayPaquete: boolean
}

/** RF-1608 · Todos los clientes activos de la cartera, por razón social. */
export interface PendientesDePresentacion {
  anio: number
  mes: number
  clientes: PendienteDeCliente[]
}

export interface ControlDeEnvioDelDespacho extends ControlDeEnvio {
  clienteId: string
  clienteRazonSocial: string
  clienteRfc: string
}

/**
 * Un plazo de balanza mensual con su procedencia (A2, RF-806 · RN-08).
 *
 * `fechaSinCalendario` es el día N del mes a secas, antes de descontar inhábiles; comparada con
 * `fechaLimite` deja explicar de dónde salió el vencimiento en vez de pedir que se crea.
 */
export interface PlazoDelPeriodo {
  tipoPersona: TipoPersona
  fechaLimite: string
  fechaSinCalendario: string
  diaInhabilRecorrido: boolean
}

/** Los plazos del periodo, uno por tipo de persona: son nacionales, no del inquilino (A2). */
export interface PlazosDelPeriodo {
  anio: number
  mes: number
  plazos: PlazoDelPeriodo[]
}

/** Quién atiende a este cliente, mirando desde el expediente (RF-104, cédula I-02, A-05). */
export interface UsuarioAsignado {
  id: string
  nombre: string
  correo: string
  rol: string
  estado: string
}

/** Cómo le fue a la descarga de madrugada en todo el despacho (RF-501, T11). */
export interface ResumenDeDescargaNocturna {
  fecha: string
  clientesActivos: number
  clientesConDescarga: number
  cfdiNuevos: number
  duplicados: number
  solicitudesFallidas: number
  solicitudesEnCola: number
  ultimaCorridaUtc?: string | null
}

export interface AlertaDeCancelacion {
  cfdiId: string
  uuid: string
  rfcEmisor: string
  nombreEmisor: string
  total: number
  fechaEmision: string
}

export interface RiesgoDeContribuyente {
  rfc: string
  situacion: string
  esRiesgoActivo: boolean
  fechaPublicacion?: string
}

export interface EmisorEnRiesgo {
  rfcEmisor: string
  nombreEmisor: string
  situacion: string
  numeroDeCfdis: number
  montoTotal: number
}

export interface ErrorDeRenglon69B {
  fila: number
  mensaje: string
}

export interface ResultadoDeSincronizacion69B {
  actualizados: number
  nuevos: number
  errores: ErrorDeRenglon69B[]
}

/** Última sincronización del listado 69-B (P-92): manual ("csv-manual") o descargada del SAT (URL). */
export interface EstadoDeSincronizacion69B {
  ultimaSincronizacionUtc: string
  origen: string
  nuevos: number
  actualizados: number
  conError: number
}

/** Respuesta del alta de cliente (P-92, RF-206): el cliente más su verificación contra el listado 69-B. */
export interface ClienteCreado extends Cliente {
  riesgo69B?: RiesgoDeContribuyente | null
}

// ---- Repositorio de CFDI (RF-503..506) ----

export type TipoComprobante = 'Ingreso' | 'Egreso' | 'Traslado' | 'Nomina' | 'Pago'
export type TipoDescargaCfdi = 'Emitidos' | 'Recibidos'

export interface CfdiDelRepositorio {
  id: string
  uuid: string
  tipo: TipoComprobante
  serie?: string
  folio?: string
  fechaEmision: string
  rfcEmisor: string
  nombreEmisor: string
  rfcReceptor: string
  nombreReceptor: string
  total: number
  moneda: string
  usoCfdi?: string
  estatus: 'Vigente' | 'Cancelado'
  esEmitido: boolean
  /** De quién es. Sobra en la vista de un cliente y da sentido a la de toda la cartera (F6). */
  clienteId: string
  clienteRazonSocial: string
  /**
   * Conciliación del comprobante (RF-601): «Contabilizado» se calcula desde las pólizas vigentes,
   * así que cancelar la póliza lo devuelve solo a «Pendiente».
   */
  estadoContabilizacion: EstadoContabilizacionCfdi
  polizaId?: string
  polizaFolioVisible?: string
}

export interface PaginaDeCfdi {
  total: number
  pagina: number
  items: CfdiDelRepositorio[]
  conteosPorTipo: Record<string, number>
}

export interface ResultadoImportacionCfdi {
  importados: number
  duplicados: number
  errores: { archivo: string; mensaje: string }[]
}

export type EstadoImportacionCfdi = 'Encolada' | 'EnProceso' | 'Terminada' | 'Error'

/**
 * Seguimiento de una importación que corre en segundo plano (P-95, RNF-04): el POST la encola
 * y la pantalla consulta el avance hasta que sea terminal. Superconjunto de ResultadoImportacionCfdi.
 */
export interface ImportacionDeCfdi extends ResultadoImportacionCfdi {
  id: string
  clienteId: string
  estado: EstadoImportacionCfdi
  total: number
  procesados: number
  totalDeErrores: number
  mensaje?: string
  terminal: boolean
  creadaEnUtc: string
  iniciadaEnUtc?: string
  terminadaEnUtc?: string
}

// ---- Pólizas y periodos (RF-401..404) ----

export type TipoPoliza = 'Ingreso' | 'Egreso' | 'Diario'

/** Marca un movimiento como el que registra un impuesto del CFDI vinculado (RF-604). */
export type EtiquetaFiscalMovimiento = 'TrasladoIva' | 'RetencionIsr' | 'RetencionIva'

/** Un renglón de un papel de trabajo fiscal (RF-1203, RF-1206): importe con su origen (RN-32). */
export interface RenglonDePapel {
  concepto: string
  etiqueta: string
  importe: number
  origen: string
}

/** Lo que no entró al papel y por qué. Se congela con el papel al sellarlo. */
export interface AdvertenciaDelPapel {
  codigo: string
  mensaje: string
}

/** Una tasa usada, con su procedencia (ADR-017): la «versión de parámetros» del sello. */
export interface ParametroFiscalUsado {
  parametro: string
  valor: number
  vigenteDesde: string
  vigenteHasta: string | null
  origen: string
  cotejado: boolean
}

/** Papel de trabajo de IVA: borrador (id y generadoEnUtc nulos) o sellado. */
export interface PapelDeIva {
  id: string | null
  anio: number
  mes: number
  generadoEnUtc: string | null
  impuestoAPagar: number
  saldoAFavorDelPeriodo: number
  saldoAFavorAnteriorAplicado: number
  origenDelSaldoAFavor: string | null
  renglones: RenglonDePapel[]
  advertencias: AdvertenciaDelPapel[]
  parametros: ParametroFiscalUsado[]
}

export interface PapelDeIvaResumen {
  id: string
  anio: number
  mes: number
  generadoEnUtc: string
  impuestoAPagar: number
  saldoAFavorDelPeriodo: number
  tieneAdvertencias: boolean
}

/** Papel de trabajo del pago provisional de ISR de personas morales: borrador (id y generadoEnUtc nulos) o sellado. */
export interface PapelDeIsr {
  id: string | null
  anio: number
  mes: number
  generadoEnUtc: string | null
  impuestoAPagar: number
  /** Pagos anteriores y retenciones que exceden el impuesto acumulado: ese mes no hay pago. */
  pagosEnExceso: number
  renglones: RenglonDePapel[]
  advertencias: AdvertenciaDelPapel[]
  parametros: ParametroFiscalUsado[]
  /**
   * La versión de los parámetros con que se calculó: el borrador la manda de vuelta al sellar, y el
   * sellado la conserva. Nula solo en los sellados antes de que se guardara.
   */
  capturaDeParametrosId: string | null
}

export interface PapelDeIsrResumen {
  id: string
  anio: number
  mes: number
  generadoEnUtc: string
  impuestoAPagar: number
  pagosEnExceso: number
  tieneAdvertencias: boolean
}

/**
 * Lo que el contador captura en el mes para el papel de ISR, cada cifra con su origen (RN-32). Un
 * campo sin valor no se manda. El coeficiente, la PTU y las pérdidas no van aquí: el servidor los
 * toma de los parámetros del ejercicio (RF-1208).
 */
export interface DatosDelPapelDeIsr {
  pagosAnteriores?: number
  origenDePagosAnteriores?: string
  retenciones?: number
  origenDeRetenciones?: string
}

/** Una pérdida fiscal pendiente de aplicar, por el ejercicio en que ocurrió (LISR art. 57). */
export interface PerdidaFiscalPendiente {
  ejercicioDeOrigen: number
  monto: number
  origen: string
}

/**
 * Un tramo de los parámetros de ISR del ejercicio (RF-1208), en su versión vigente: rige desde
 * `aplicaDesdeMes` hasta el mes anterior al siguiente tramo.
 */
export interface ParametrosDeIsr {
  id: string
  ejercicio: number
  aplicaDesdeMes: number
  coeficiente: number
  origenDelCoeficiente: string
  ptuPagada: number
  origenDeLaPtu: string | null
  perdidas: PerdidaFiscalPendiente[]
  /** Un instante: se convierte a hora local. */
  capturadaEnUtc: string
}

/**
 * Lo que se manda al capturar un tramo de los parámetros: completo, porque guardar crea una versión
 * nueva del tramo. El coeficiente ausente el servidor lo rechaza, no lo toma por cero.
 */
export interface CapturaDeParametrosDeIsr {
  coeficiente?: number
  origenDelCoeficiente?: string
  ptuPagada?: number
  origenDeLaPtu?: string
  perdidas: PerdidaFiscalPendiente[]
  aplicaDesdeMes: number
  /** La versión vigente de ese tramo que se tenía a la vista, o nula si es nuevo: si cambió, PARAMETROS_CAMBIARON. */
  basadaEnCapturaId: string | null
}

export type TipoDeDiferenciaDeConciliacion =
  | 'CfdiSinPoliza'
  | 'IvaSinCfdi'
  | 'IvaDistinto'
  | 'CfdiCanceladoConIvaRegistrado'
  | 'CobroSinComplementoDePago'

/**
 * Una diferencia de la cédula de conciliación (RF-1205). `fecha` es la que la ancla al periodo: la
 * emisión del CFDI si no tiene póliza, la de la póliza en todas las demás.
 */
export interface DiferenciaDeConciliacion {
  tipo: TipoDeDiferenciaDeConciliacion
  uuid: string | null
  polizaId: string | null
  polizaFolio: string | null
  fecha: string
  contraparte: string | null
  total: number | null
  ivaSegunCfdi: number | null
  ivaSegunContabilidad: number | null
  mensaje: string
}

/** Algo que impidió cotejar una parte, o que cambia cómo leer la cédula. */
export interface AdvertenciaDeConciliacion {
  codigo: string
  mensaje: string
}

/** Cuánto se miró: una cédula sin diferencias solo dice algo si dice también cuánto revisó. */
export interface AlcanceDeLaConciliacion {
  comprobantes: number
  descartados: number
  polizas: number
}

export interface CedulaDeConciliacion {
  anio: number
  mes: number
  revisado: AlcanceDeLaConciliacion
  diferencias: DiferenciaDeConciliacion[]
  advertencias: AdvertenciaDeConciliacion[]
  /** Lo que la cédula deliberadamente no mira. */
  noRevisa: string[]
}

/** Una serie de folios del cliente (RF-1005), con el folio que entregaría ahora y sus pendientes. */
export interface SerieDeFolios {
  id: string
  serie: string
  descripcion: string | null
  folioInicial: number
  activa: boolean
  siguienteFolio: number
  foliosPendientes: number
  creadaEnUtc: string
}

/** Reservado: apartado y sin timbre (pendiente). Consumido: se timbró. Descartado: con su motivo. */
export type EstadoDeFolio = 'Reservado' | 'Consumido' | 'Descartado'

/** Un folio apartado de una serie. Nunca se borra: se consume o se descarta (RF-1005). */
export interface FolioApartado {
  id: string
  serieDeFoliosId: string
  serie: string
  folio: number
  estado: EstadoDeFolio
  reservadoEnUtc: string
  proposito: string | null
  uuidTimbrado: string | null
  consumidoEnUtc: string | null
  motivoDescarte: string | null
  descartadoEnUtc: string | null
}

export interface ResumenPeriodo {
  anio: number
  mes: number
  estado: 'Abierto' | 'Cerrado'
  polizas: number
}

/** Datos de pago y moneda extranjera: opcionales, por movimiento, no por póliza (RF-403, RN-12). */
export type MetodoDePago = 'Transferencia' | 'Cheque' | 'Efectivo'

export interface MovimientoPoliza {
  id: string
  cuentaId: string
  cuentaNumero: string
  cuentaNombre: string
  descripcion?: string
  cargo: number
  abono: number
  moneda: string
  tipoCambio?: number
  metodoDePago?: MetodoDePago
  banco?: string
  cuenta?: string
  referencia?: string
  etiquetaFiscal?: EtiquetaFiscalMovimiento
  /** Tercero del pago (RF-803): solo con estos tres datos completos y un banco válido se genera el nodo Cheque/Transferencia del PL. */
  rfcTercero?: string
  nombreTercero?: string
  bancoCodigoSat?: string
}

export interface MovimientoPolizaRequest {
  cuentaId: string
  cargo: number
  abono: number
  descripcion?: string
  moneda?: string
  tipoCambio?: number
  metodoDePago?: MetodoDePago
  banco?: string
  cuenta?: string
  referencia?: string
  etiquetaFiscal?: EtiquetaFiscalMovimiento
  rfcTercero?: string
  nombreTercero?: string
  bancoCodigoSat?: string
}

export interface BucketDeCongruencia {
  concepto: string
  segunCfdi: number
  registradoEnPoliza: number
  diferencia: number
  congruente: boolean
}

export interface CongruenciaFiscalPoliza {
  buckets: BucketDeCongruencia[]
  todoCongruente: boolean
}

export interface Poliza {
  id: string
  folioVisible: string
  tipo: TipoPoliza
  fecha: string
  concepto: string
  estado: 'Vigente' | 'Cancelada'
  motivoCancelacion?: string
  sustituyeAPolizaId?: string
  totalCargos: number
  totalAbonos: number
  movimientos: MovimientoPoliza[]
  cfdis: { uuid: string; rfcEmisor?: string; montoTotal?: number }[]
  congruenciaFiscal?: CongruenciaFiscalPoliza | null
}

// ---- Plantillas de póliza recurrentes y generación programada (RF-405) ----

export type PeriodicidadPlantilla = 'Mensual'
export type EstadoPlantilla = 'Activa' | 'Desactivada'
export type EstadoGeneracionPlantilla = 'Pendiente' | 'Aprobada' | 'Rechazada'

export interface RenglonDePlantilla {
  id: string
  cuentaId: string
  cuentaNumero: string
  cuentaNombre: string
  cargo: number
  abono: number
  descripcion?: string
}

export interface RenglonDePlantillaRequest {
  cuentaId: string
  cargo: number
  abono: number
  descripcion?: string
}

export interface PlantillaDePoliza {
  id: string
  clienteId: string
  tipo: TipoPoliza
  concepto: string
  periodicidad: PeriodicidadPlantilla
  estado: EstadoPlantilla
  totalCargos: number
  totalAbonos: number
  renglones: RenglonDePlantilla[]
}

export interface GeneracionDePlantilla {
  id: string
  clienteId: string
  clienteRazonSocial: string
  plantillaId: string
  plantillaConcepto: string
  anio: number
  mes: number
  estado: EstadoGeneracionPlantilla
  creadaEnUtc: string
  revisadaEnUtc?: string
  motivoRechazo?: string
  polizaGeneradaId?: string
}

// ---- Reportes (RF-701, RF-702, RF-704) ----

export type ClasificacionFinanciera =
  | 'ActivoCirculante' | 'ActivoNoCirculante' | 'PasivoCirculante' | 'PasivoNoCirculante' | 'Capital'
  | 'Ingresos' | 'Costos' | 'Gastos' | 'ResultadoIntegralDeFinanciamiento' | 'CuentaDeOrden'

export interface RenglonDeBalanza {
  cuentaId: string
  numero: string
  nombre: string
  nivel: number
  esDeRegistro: boolean
  naturaleza: Naturaleza
  clasificacion?: ClasificacionFinanciera
  /** Código agrupador del SAT de la cuenta (RF-401): la columna que se coteja contra la balanza del Anexo 24. */
  codigoAgrupadorSat?: string
  saldoInicial: number
  cargos: number
  abonos: number
  saldoFinal: number
}

export interface Balanza {
  anio: number
  mes: number
  renglones: RenglonDeBalanza[]
  totalCargos: number
  totalAbonos: number
  cuadrada: boolean
}

/** Ajuste de cierre de ejercicio (periodo 13, RF-802): solo cuenta, importe y descripción — sin CFDI ni datos de pago. */
export interface MovimientoDeAjusteRequest {
  cuentaId: string
  cargo: number
  abono: number
  descripcion?: string
}

export interface MovimientoDeMayor {
  fecha: string
  polizaId: string
  polizaFolioVisible: string
  descripcion?: string
  cargo: number
  abono: number
  saldoAcumulado: number
}

export interface LibroMayor {
  cuentaId: string
  cuentaNumero: string
  cuentaNombre: string
  naturaleza: Naturaleza
  saldoInicial: number
  movimientos: MovimientoDeMayor[]
  saldoFinal: number
}

// ---- Balance general, estado de resultados y congruencia (RF-703, RF-706, RN-13) ----

export interface RenglonComparativo {
  numero: string
  nombre: string
  monto: number
  montoComparativo: number
}

export interface SeccionComparativa {
  nombre: string
  renglones: RenglonComparativo[]
  total: number
  totalComparativo: number
}

export interface BalanceGeneral {
  anio: number
  mes: number
  anioComparativo: number
  mesComparativo: number
  activoCirculante: SeccionComparativa
  activoNoCirculante: SeccionComparativa
  totalActivo: number
  totalActivoComparativo: number
  /** RN-13: pasivo a corto plazo (agrupadores 200-250) y a largo plazo (251-299). */
  pasivoCirculante: SeccionComparativa
  pasivoNoCirculante: SeccionComparativa
  totalPasivo: number
  totalPasivoComparativo: number
  capital: SeccionComparativa
  totalPasivoMasCapital: number
  totalPasivoMasCapitalComparativo: number
  cuadrado: boolean
  cuadradoComparativo: boolean
}

export interface EstadoDeResultados {
  anio: number
  mes: number
  anioComparativo: number
  mesComparativo: number
  ingresos: SeccionComparativa
  costos: SeccionComparativa
  utilidadBruta: number
  utilidadBrutaComparativo: number
  gastos: SeccionComparativa
  utilidadDeOperacion: number
  utilidadDeOperacionComparativo: number
  resultadoIntegralDeFinanciamiento: SeccionComparativa
  utilidadNeta: number
  utilidadNetaComparativo: number
}

/** RF-706 · Una cuenta que la congruencia sumó como «Resultado del ejercicio» (agrupador 305). */
export interface CuentaDelResultadoDelEjercicio {
  numero: string
  nombre: string
  codigoAgrupadorSat: string
  saldo: number
}

export interface CongruenciaGlobal {
  totalActivo: number
  totalPasivoMasCapital: number
  activoIgualPasivoMasCapital: boolean
  utilidadSegunEstadoDeResultados: number
  utilidadSegunBalance: number
  utilidadCongruente: boolean
  /** De dónde sale `utilidadSegunBalance`. Vacía: ninguna cuenta tiene el agrupador 305, no hay contra qué comparar. */
  cuentasDelResultadoDelEjercicio: CuentaDelResultadoDelEjercicio[]
}

// ---- Panel de contabilización (RF-601) ----

export type EstadoContabilizacionCfdi = 'Pendiente' | 'Contabilizado' | 'Descartado'

export interface ResumenDePanel {
  pendientes: number
  contabilizados: number
  descartados: number
  total: number
}

export interface CfdiDelPanel {
  id: string
  uuid: string
  tipo: TipoComprobante
  serie?: string
  folio?: string
  fechaEmision: string
  rfcEmisor: string
  nombreEmisor: string
  rfcReceptor: string
  nombreReceptor: string
  total: number
  moneda: string
  esEmitido: boolean
  estado: EstadoContabilizacionCfdi
  polizaId?: string
  polizaFolioVisible?: string
  motivoDescarte?: string
  /** El servidor ya sabe si el emisor tiene regla activa: la cola lo muestra antes de pedir la sugerencia. */
  tieneReglaActiva: boolean
  reglaDescripcion?: string
}

// ---- Contabilización asistida (RF-602, RF-603) ----

export interface ReglaDeContabilizacion {
  id: string
  rfcEmisor: string
  descripcion?: string
  estado: 'Activa' | 'Desactivada'
  cuentaGastoId: string
  cuentaGastoTexto: string
  cuentaContrapartidaId: string
  cuentaContrapartidaTexto: string
  cuentaIvaAcreditableId?: string
  cuentaIvaAcreditableTexto?: string
  cuentaIsrRetenidoId?: string
  cuentaIsrRetenidoTexto?: string
  cuentaIvaRetenidoId?: string
  cuentaIvaRetenidoTexto?: string
}

export interface MovimientoSugerido {
  cuentaId: string
  cuentaNumero: string
  cuentaNombre: string
  cargo: number
  abono: number
  etiquetaFiscal?: EtiquetaFiscalMovimiento
}

export interface PolizaSugerida {
  reglaId: string
  rfcEmisor: string
  nombreEmisor: string
  concepto: string
  fecha: string
  uuids: string[]
  movimientos: MovimientoSugerido[]
  totalCfdi: number
}

// ------------------------------------------------------------- Endpoints

export const api = {
  despachoActual: () => peticion<Despacho>('/api/despachos/actual'),

  tablero: () => peticion<ResumenDeCliente[]>('/api/despachos/actual/tablero'),

  // ---- Notificaciones (RF-902) ----

  notificaciones: (soloNoLeidas = false) =>
    peticion<Notificacion[]>(`/api/notificaciones/?soloNoLeidas=${soloNoLeidas}`),

  generarNotificaciones: () => peticion<{ creadas: number }>('/api/notificaciones/generar', { method: 'POST' }),

  marcarNotificacionLeida: (id: string) =>
    peticion<void>(`/api/notificaciones/${id}/marcar-leida`, { method: 'POST' }),

  // ---- Aviso de privacidad (LFPDPPP, derechos ARCO — P-102) ----

  avisoDePrivacidad: () => peticion<AvisoDePrivacidad>('/api/aviso-de-privacidad'),

  aceptarAvisoDePrivacidad: () => peticion<void>('/api/aviso-de-privacidad/aceptar', { method: 'POST' }),

  listarClientes: (filtros: FiltrosDeCartera & {
    pagina?: number
    /** El API acota entre 1 y 200; por omisión devuelve 50. */
    tamano?: number
  } = {}) => {
    const params = parametrosDeCartera(filtros)
    params.set('pagina', String(filtros.pagina ?? 1))
    if (filtros.tamano) params.set('tamano', String(filtros.tamano))
    return peticion<Pagina<Cliente>>(`/api/clientes?${params}`)
  },

  /**
   * La cartera filtrada, en Excel o PDF (RF-705). Recibe los MISMOS filtros que
   * `listarClientes` y los arma con la misma función: el archivo es lo que se ve en pantalla,
   * sin paginar. El PDF sale con menos columnas y la razón social recortada —lo decide el
   * servidor— porque con las ocho del Excel las celdas se encimarían en hoja carta.
   *
   * El correo de contacto NO va en el archivo: es dato personal (LFPDPPP) y un listado de
   * cartera circula por correo con facilidad.
   */
  exportarClientes: (filtros: FiltrosDeCartera = {}, formato: 'excel' | 'pdf' = 'excel') => {
    const params = parametrosDeCartera(filtros)
    params.set('formato', formato)
    return descargarArchivo(
      `/api/clientes/exportacion?${params}`,
      `cartera.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    )
  },

  crearCliente: (cliente: {
    tipoPersona: TipoPersona
    rfc: string
    razonSocial: string
    regimenFiscal: string
    codigoPostalFiscal: string
    fechaInicioOperaciones: string
    correoContacto?: string
  }) => peticion<ClienteCreado>('/api/clientes', { method: 'POST', body: JSON.stringify(cliente) }),

  /**
   * Corrige los datos fiscales del expediente (RF-201). Devuelve el cliente ya actualizado,
   * así que la pantalla repinta con lo que respondió el servidor y no con lo que tecleó el
   * contador: el API normaliza (recorta la razón social, pasa el correo a minúsculas) y
   * resuelve la descripción del régimen contra el catálogo vigente.
   *
   * Errores que la pantalla debe saber contar: 400 con código RAZON_SOCIAL_REQUERIDA,
   * CP_INVALIDO o el del régimen incompatible con el tipo de persona del cliente; 403 para
   * Solo lectura; 404 si el cliente no existe o está fuera de la cartera del colaborador.
   */
  editarCliente: (id: string, datos: EdicionDeCliente) =>
    peticion<Cliente>(`/api/clientes/${id}`, { method: 'PUT', body: JSON.stringify(datos) }),

  archivarCliente: (id: string) => peticion<void>(`/api/clientes/${id}/archivar`, { method: 'POST' }),

  reactivarCliente: (id: string) => peticion<void>(`/api/clientes/${id}/reactivacion`, { method: 'POST' }),

  regimenesFiscales: (tipoPersona?: TipoPersona) =>
    peticion<RegimenFiscal[]>(
      `/api/catalogos/regimenes-fiscales${tipoPersona ? `?tipoPersona=${tipoPersona}` : ''}`,
    ),

  obtenerCliente: (id: string) => peticion<Cliente>(`/api/clientes/${id}`),

  // ---- e.firma (RF-203, RF-204) ----

  credencialDeCliente: async (clienteId: string): Promise<EstadoCredencial | null> => {
    try {
      return await peticion<EstadoCredencial>(`/api/clientes/${clienteId}/credencial-fiscal`)
    } catch (e) {
      if (e instanceof ErrorDeApi && e.estado === 404) return null
      throw e
    }
  },

  /**
   * Sube la e.firma. Va como multipart y sin encabezado Content-Type manual:
   * el navegador debe fijar el boundary. El material nunca se guarda en el navegador.
   * La contraseña viaja en el cuerpo, nunca en la URL: las URL quedan en registros de acceso.
   */
  cargarCredencial: async (clienteId: string, datos: FormData): Promise<ResultadoDeCarga> => {
    const token = await tokenDeSesion(obtenerToken)

    const cuerpo = new FormData()
    cuerpo.append('cer', datos.get('cer') as File)
    cuerpo.append('key', datos.get('key') as File)
    cuerpo.append('contrasena', String(datos.get('contrasena')))
    cuerpo.append('proposito', String(datos.get('proposito')))

    const respuesta = await fetchDeApi(`${baseUrl}/api/clientes/${clienteId}/credencial-fiscal`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: cuerpo,
    })

    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }

    return (await respuesta.json()) as ResultadoDeCarga
  },

  revocarCredencial: (clienteId: string, credencialId: string, motivo: string) =>
    peticion<void>(
      `/api/clientes/${clienteId}/credencial-fiscal/${credencialId}?motivo=${encodeURIComponent(motivo)}`,
      { method: 'DELETE' },
    ),

  // ---- Sello digital, CSD (RF-1001, RF-1002 · ADR-013) ----
  // Superficie aparte de la e.firma aunque el material se parezca: por aquí el certificado se
  // entrega al proveedor de timbrado y por allá nunca sale de ContaQuick.

  selloDigitalDeCliente: (clienteId: string) =>
    peticion<EstadoDelSelloDigital>(`/api/clientes/${clienteId}/sello-digital`),

  /**
   * Sube el CSD. Igual que la e.firma: multipart sin Content-Type manual (el navegador fija el
   * boundary), contraseña en el cuerpo y nunca en la URL, nada del material queda en el navegador.
   */
  cargarSelloDigital: async (clienteId: string, datos: FormData): Promise<ResultadoDelSello> => {
    const token = await tokenDeSesion(obtenerToken)

    const cuerpo = new FormData()
    cuerpo.append('cer', datos.get('cer') as File)
    cuerpo.append('key', datos.get('key') as File)
    cuerpo.append('contrasena', String(datos.get('contrasena')))
    cuerpo.append('proposito', String(datos.get('proposito')))

    const respuesta = await fetchDeApi(`${baseUrl}/api/clientes/${clienteId}/sello-digital`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: cuerpo,
    })

    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }

    return (await respuesta.json()) as ResultadoDelSello
  },

  sincronizarSelloDigital: (clienteId: string) =>
    peticion<EstadoDelSelloDigital>(`/api/clientes/${clienteId}/sello-digital/sincronizar`, { method: 'POST' }),

  revocarSelloDigital: (clienteId: string, credencialId: string, motivo: string) =>
    peticion<void>(
      `/api/clientes/${clienteId}/sello-digital/${credencialId}?motivo=${encodeURIComponent(motivo)}`,
      { method: 'DELETE' },
    ),

  // ---- Catálogos de emisión de CFDI (RF-1003 · ADR-014) ----
  // Devuelven ENVOLTURA, no arreglo plano como regimenesFiscales: quien captura necesita saber
  // si el catálogo está cargado y de dónde salió, no solo sus claves.

  catalogoDeEmision: (ruta: RutaDeCatalogo, opciones?: { buscar?: string; tipoPersona?: TipoPersona }) => {
    const parametros = new URLSearchParams()
    if (opciones?.buscar) parametros.set('buscar', opciones.buscar)
    if (opciones?.tipoPersona) parametros.set('tipoPersona', opciones.tipoPersona)
    const cola = parametros.toString()
    return peticion<CatalogoCortoDelSat>(`/api/catalogos/${ruta}${cola ? `?${cola}` : ''}`)
  },

  buscarClavesProdServ: (buscar?: string, pagina = 1, tamano = 50) => {
    const parametros = new URLSearchParams({ pagina: String(pagina), tamano: String(tamano) })
    if (buscar) parametros.set('buscar', buscar)
    return peticion<PaginaDeClaves<ClaveProdServ>>(`/api/catalogos/claves-prod-serv?${parametros}`)
  },

  buscarClavesUnidad: (buscar?: string, pagina = 1, tamano = 50) => {
    const parametros = new URLSearchParams({ pagina: String(pagina), tamano: String(tamano) })
    if (buscar) parametros.set('buscar', buscar)
    return peticion<PaginaDeClaves<ClaveUnidad>>(`/api/catalogos/claves-unidad?${parametros}`)
  },

  estadoDeCatalogos: () => peticion<ResumenDeCatalogo[]>('/api/catalogos/estado'),

  credencialesPorVencer: (dias = 90) =>
    peticion<{ total: number; items: EstadoCredencial[] }>(`/api/credenciales/por-vencer?dias=${dias}`),

  // ---- Constancia de Situación Fiscal, CSF (RF-202) ----

  /**
   * Extracción asistida: lee un PDF de CSF y sugiere RFC/régimen/CP para prellenar el alta.
   * No guarda nada — el propio formulario de alta es quien decide qué hacer con la sugerencia.
   */
  extraerCsf: async (archivoPdf: File): Promise<ResultadoExtraccionCsf> => {
    const token = await tokenDeSesion(obtenerToken)
    const cuerpo = new FormData()
    cuerpo.append('archivo', archivoPdf)

    const respuesta = await fetchDeApi(`${baseUrl}/api/csf/extraccion`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: cuerpo,
    })

    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }

    return (await respuesta.json()) as ResultadoExtraccionCsf
  },

  /** Si el cliente ya tiene una CSF resguardada en su expediente. */
  estadoConstanciaFiscal: (clienteId: string) =>
    peticion<{ tieneConstancia: boolean }>(`/api/clientes/${clienteId}/constancia-fiscal/estado`)
      .then((r) => r.tieneConstancia),

  /** Sube (o reemplaza) la CSF del cliente en su expediente. */
  cargarConstanciaFiscal: async (clienteId: string, archivoPdf: File): Promise<void> => {
    const token = await tokenDeSesion(obtenerToken)
    const cuerpo = new FormData()
    cuerpo.append('archivo', archivoPdf)

    const respuesta = await fetchDeApi(`${baseUrl}/api/clientes/${clienteId}/constancia-fiscal`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: cuerpo,
    })

    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }
  },

  /** Descarga la CSF del expediente y hace que el navegador la guarde. */
  descargarConstanciaFiscal: (clienteId: string) =>
    descargarArchivo(`/api/clientes/${clienteId}/constancia-fiscal`, 'constancia-fiscal.pdf'),

  // ---- Catálogo de cuentas (RF-301..303) ----

  // El periodo es opcional a propósito: sin él la respuesta es la de siempre y el backend no
  // paga la consulta de movimientos. Van los dos o ninguno; con solo uno el backend los ignora.
  catalogoDeCuentas: (clienteId: string, anio?: number, mes?: number) =>
    peticion<CuentaDeCatalogo[]>(
      `/api/clientes/${clienteId}/catalogo-cuentas/` +
        (anio !== undefined && mes !== undefined ? `?anio=${anio}&mes=${mes}` : ''),
    ),

  validacionCt: (clienteId: string) =>
    peticion<ValidacionCt>(`/api/clientes/${clienteId}/catalogo-cuentas/validacion-ct`),

  generarPlantilla: (clienteId: string, plantilla: Plantilla) =>
    peticion<{ cuentasCreadas: number }>(`/api/clientes/${clienteId}/catalogo-cuentas/plantilla`, {
      method: 'POST',
      body: JSON.stringify({ plantilla }),
    }),

  /**
   * Borra todas las cuentas del catálogo del cliente para generarlo de nuevo desde la plantilla. Solo el
   * administrador, y solo si ninguna cuenta tiene pólizas, reglas, plantillas de póliza ni cuentas
   * bancarias, y si el catálogo no se envió al SAT: un catálogo que nunca se usó todavía no es
   * contabilidad (ADR-018). Existe porque las plantillas anteriores al 20/10/2026 ligaban cuentas a
   * códigos agrupadores equivocados.
   */
  reiniciarCatalogo: (clienteId: string, motivo: string) =>
    peticion<{ cuentasBorradas: number }>(`/api/clientes/${clienteId}/catalogo-cuentas/reinicio`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),

  crearCuenta: (
    clienteId: string,
    cuenta: {
      numero: string
      nombre: string
      cuentaPadreId?: string
      naturaleza?: Naturaleza
      codigoAgrupadorSat?: string
    },
  ) =>
    peticion<CuentaDeCatalogo>(`/api/clientes/${clienteId}/catalogo-cuentas/`, {
      method: 'POST',
      body: JSON.stringify(cuenta),
    }),

  editarCuenta: (
    clienteId: string,
    cuentaId: string,
    cambios: { nombre: string; naturaleza: Naturaleza; codigoAgrupadorSat?: string },
  ) =>
    peticion<ResultadoEdicionCuenta>(`/api/clientes/${clienteId}/catalogo-cuentas/${cuentaId}`, {
      method: 'PUT',
      body: JSON.stringify(cambios),
    }),

  desactivarCuenta: (clienteId: string, cuentaId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/catalogo-cuentas/${cuentaId}/desactivar`, { method: 'POST' }),

  reactivarCuenta: (clienteId: string, cuentaId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/catalogo-cuentas/${cuentaId}/reactivar`, { method: 'POST' }),

  importarCatalogo: async (clienteId: string, archivo: File): Promise<ResultadoImportacion> => {
    const token = await tokenDeSesion(obtenerToken)
    const cuerpo = new FormData()
    cuerpo.append('archivo', archivo)

    const respuesta = await fetchDeApi(`${baseUrl}/api/clientes/${clienteId}/catalogo-cuentas/importacion`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: cuerpo,
    })

    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }

    return (await respuesta.json()) as ResultadoImportacion
  },

  /** Descarga el ZIP del XML CT; el navegador guarda el archivo con la nomenclatura oficial. */
  descargarCt: async (clienteId: string, anio: number, mes: number): Promise<void> => {
    const token = await tokenDeSesion(obtenerToken)
    const respuesta = await fetchDeApi(
      `${baseUrl}/api/clientes/${clienteId}/catalogo-cuentas/ct?anio=${anio}&mes=${mes}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )

    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }

    const nombre =
      respuesta.headers.get('Content-Disposition')?.match(/filename="?([^\";]+)"?/)?.[1] ?? 'catalogo-ct.zip'
    const enlace = document.createElement('a')
    enlace.href = URL.createObjectURL(await respuesta.blob())
    enlace.download = nombre
    enlace.click()
    URL.revokeObjectURL(enlace.href)
  },

  /** Descarga el ZIP del XML de balanza (BN/BC); el navegador guarda el archivo con la nomenclatura oficial. */
  descargarBalanza: async (
    clienteId: string,
    anio: number,
    mesPeriodo: number,
    tipoEnvio: TipoEnvioBalanza,
    fechaModBal?: string,
  ): Promise<void> => {
    const token = await tokenDeSesion(obtenerToken)
    const parametros = new URLSearchParams({ anio: String(anio), mesPeriodo: String(mesPeriodo), tipoEnvio })
    if (fechaModBal) parametros.set('fechaModBal', fechaModBal)

    const respuesta = await fetchDeApi(
      `${baseUrl}/api/clientes/${clienteId}/catalogo-cuentas/balanza-electronica?${parametros.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )

    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }

    const nombre =
      respuesta.headers.get('Content-Disposition')?.match(/filename="?([^\";]+)"?/)?.[1] ?? 'balanza.zip'
    const enlace = document.createElement('a')
    enlace.href = URL.createObjectURL(await respuesta.blob())
    enlace.download = nombre
    enlace.click()
    URL.revokeObjectURL(enlace.href)
  },

  // ---- PL / XF / XC para solicitudes del SAT (RF-803, RF-804) ----

  descargarPolizasPeriodo: (
    clienteId: string,
    anio: number,
    mes: number,
    tipoSolicitud: TipoSolicitudFiscalizacion,
    numOrden?: string,
    numTramite?: string,
  ) =>
    descargarArchivo(
      `/api/clientes/${clienteId}/catalogo-cuentas/polizas-periodo?${parametrosSolicitud(anio, mes, tipoSolicitud, numOrden, numTramite)}`,
      'polizas-periodo.zip',
    ),

  descargarAuxiliarFolios: (
    clienteId: string,
    anio: number,
    mes: number,
    tipoSolicitud: TipoSolicitudFiscalizacion,
    numOrden?: string,
    numTramite?: string,
  ) =>
    descargarArchivo(
      `/api/clientes/${clienteId}/catalogo-cuentas/auxiliar-folios?${parametrosSolicitud(anio, mes, tipoSolicitud, numOrden, numTramite)}`,
      'auxiliar-folios.zip',
    ),

  descargarAuxiliarCuentas: (
    clienteId: string,
    anio: number,
    mes: number,
    tipoSolicitud: TipoSolicitudFiscalizacion,
    numOrden?: string,
    numTramite?: string,
  ) =>
    descargarArchivo(
      `/api/clientes/${clienteId}/catalogo-cuentas/auxiliar-cuentas?${parametrosSolicitud(anio, mes, tipoSolicitud, numOrden, numTramite)}`,
      'auxiliar-cuentas.zip',
    ),

  // ---- Control de envíos del Anexo 24 (RF-805) ----

  listarControlDeEnvios: (clienteId: string) => peticion<ControlDeEnvio[]>(`/api/clientes/${clienteId}/control-envios/`),

  /**
   * RF-1600: recupera el archivo que hay que subir al Buzón, regenerado al momento y sin tocar
   * el estado del ciclo de envío (RN-58). Devuelve si lo regenerado difiere de lo que se
   * presentó, para que la pantalla lo advierta en vez de entregarlo en silencio (RN-60).
   */
  /** RF-1603 · Qué se sube, dónde, con qué firma y hasta cuándo. */
  guiaDePresentacion: (clienteId: string, controlId: string) =>
    peticion<GuiaDePresentacion>(`/api/clientes/${clienteId}/control-envios/${controlId}/guia`),

  /** RF-1604 · Resguarda el archivo del acuse que devolvió el SAT (PDF o ZIP). */
  resguardarAcuseArchivo: async (clienteId: string, controlId: string, archivo: File) => {
    const cuerpo = new FormData()
    cuerpo.append('archivo', archivo)
    const token = await tokenDeSesion(obtenerToken)
    // Multipart y sin Content-Type manual: el navegador pone el boundary, y ponerlo a mano
    // rompe la subida.
    const respuesta = await fetchDeApi(
      `${baseUrl}/api/clientes/${clienteId}/control-envios/${controlId}/acuse/archivo`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: cuerpo })

    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }

    return (await respuesta.json()) as { nombre: string; resguardadoEnUtc: string }
  },

  /** RF-1604 · Descarga el acuse resguardado (RN-09: se conserva al menos 5 años). */
  descargarAcuseArchivo: (clienteId: string, controlId: string) =>
    descargarArchivo(`/api/clientes/${clienteId}/control-envios/${controlId}/acuse/archivo`, 'acuse-sat.pdf'),

  /**
   * A5 · Por omisión baja el paquete RESGUARDADO, que es el que se presentó. `resguardado`
   * dice cuál de los dos llegó; `divergente` solo tiene sentido cuando se regeneró, porque
   * es entonces cuando se compara la huella actual contra la presentada.
   */
  descargarArchivoDeEnvio: async (
    clienteId: string, controlId: string, regenerar = false,
  ): Promise<{ divergente: boolean; resguardado: boolean }> => {
    const cabeceras = await descargarArchivo(
      `/api/clientes/${clienteId}/control-envios/${controlId}/archivo${regenerar ? '?regenerar=true' : ''}`,
      'anexo24.zip')
    return {
      divergente: cabeceras.get('X-ContaQuick-Divergente') === '1',
      resguardado: cabeceras.get('X-ContaQuick-Resguardado') === '1',
    }
  },

  marcarEnviado: (clienteId: string, controlId: string) =>
    peticion<ControlDeEnvio>(`/api/clientes/${clienteId}/control-envios/${controlId}/marcar-enviado`, { method: 'POST' }),

  registrarAcuse: (
    clienteId: string,
    controlId: string,
    acuse: { aceptado: boolean; folio?: string; sello?: string; motivoRechazo?: string },
  ) =>
    peticion<ControlDeEnvio>(`/api/clientes/${clienteId}/control-envios/${controlId}/acuse`, {
      method: 'POST',
      body: JSON.stringify(acuse),
    }),

  codigosAgrupadores: (buscar?: string) =>
    peticion<CodigoAgrupador[]>(
      `/api/catalogos/codigos-agrupadores${buscar ? `?buscar=${encodeURIComponent(buscar)}` : ''}`,
    ),

  // ---- Descarga masiva ante el SAT (RF-501, RF-502) ----

  listarDescargasMasivas: (clienteId: string) =>
    peticion<SolicitudDeDescarga[]>(`/api/clientes/${clienteId}/descarga-masiva/`),

  solicitarDescargaMasiva: (
    clienteId: string,
    solicitud: { tipo: TipoDescargaCfdi; fechaInicial: string; fechaFinal: string },
  ) =>
    peticion<ResultadoDeLote>(`/api/clientes/${clienteId}/descarga-masiva/`, {
      method: 'POST',
      body: JSON.stringify(solicitud),
    }),

  verificarDescargaMasiva: (clienteId: string, solicitudId: string, forzar = false) =>
    peticion<SolicitudDeDescarga>(
      `/api/clientes/${clienteId}/descarga-masiva/${solicitudId}/verificacion?forzar=${forzar}`,
      { method: 'POST' },
    ),

  cotejoDeCompletitud: (clienteId: string, solicitudId: string) =>
    peticion<ResultadoDeCotejo>(`/api/clientes/${clienteId}/descarga-masiva/${solicitudId}/completitud`),

  // ---- Estatus de CFDI y listado 69-B (RF-206, RF-505, RF-507) ----

  verificarEstatusDeCfdi: (clienteId: string) =>
    peticion<ResumenDeVerificacion>(`/api/clientes/${clienteId}/cfdi/verificacion-de-estatus`, { method: 'POST' }),

  alertasDeCancelacion: (clienteId: string) =>
    peticion<AlertaDeCancelacion[]>(`/api/clientes/${clienteId}/cfdi/alertas-de-cancelacion`),

  atenderAlertaDeCancelacion: (clienteId: string, cfdiId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/cfdi/alertas-de-cancelacion/${cfdiId}/atendida`, { method: 'POST' }),

  emisoresEnRiesgo: (clienteId: string) =>
    peticion<EmisorEnRiesgo[]>(`/api/clientes/${clienteId}/cfdi/emisores-en-riesgo`),

  // ---- Complemento de recepción de pagos del acervo histórico (RF-1201) ----

  /**
   * Cuántos CFDI del cliente siguen sin que se les haya mirado el complemento de pago. Mientras
   * ese número no sea cero, cualquier IVA en base de flujo del cliente está incompleto.
   */
  complementosDePagoPendientes: (clienteId: string) =>
    peticion<{ pendientes: number }>(`/api/clientes/${clienteId}/cfdi/complementos-pendientes`),

  /**
   * Relee el XML ya custodiado para indexar el complemento de pago que falte. Es idempotente: un
   * comprobante ya revisado no vuelve a la cola y una parcialidad ya indexada no se duplica.
   * `maximo` acota el lote; el backend usa 500 si no se manda.
   */
  reprocesarComplementosDePago: (clienteId: string, maximo?: number) =>
    peticion<ResumenDeReproceso>(
      `/api/clientes/${clienteId}/cfdi/reproceso-de-complementos`
        + (maximo === undefined ? '' : `?maximo=${maximo}`),
      { method: 'POST' },
    ),

  riesgo69bDelCliente: async (clienteId: string): Promise<RiesgoDeContribuyente | null> => {
    try {
      return await peticion<RiesgoDeContribuyente>(`/api/clientes/${clienteId}/riesgo-69b`)
    } catch (e) {
      if (e instanceof ErrorDeApi && e.estado === 404) return null
      throw e
    }
  },

  sincronizarLista69b: async (archivo: File): Promise<ResultadoDeSincronizacion69B> => {
    const token = await tokenDeSesion(obtenerToken)
    const cuerpo = new FormData()
    cuerpo.append('archivo', archivo)
    const respuesta = await fetchDeApi(`${baseUrl}/api/listado-69b/sincronizacion`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: cuerpo,
    })
    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }
    return (await respuesta.json()) as ResultadoDeSincronizacion69B
  },

  /** A-06: descarga el listado publicado por el SAT y lo sincroniza ahora (solo Administrador). */
  sincronizarLista69bDesdeSat: () =>
    peticion<ResumenDeSincronizacionDesdeElSat>('/api/listado-69b/sincronizacion-desde-sat', { method: 'POST' }),

  /** P-92: cuándo y de dónde se sincronizó el listado 69-B por última vez; null si nunca. */
  estadoDeLista69b: async (): Promise<EstadoDeSincronizacion69B | null> => {
    try {
      return await peticion<EstadoDeSincronizacion69B>('/api/listado-69b/estado')
    } catch (e) {
      if (e instanceof ErrorDeApi && e.estado === 404) return null
      throw e
    }
  },

  // ---- Repositorio de CFDI (RF-503..506) ----

  listarCfdi: (
    clienteId: string,
    filtros: { tipo?: TipoComprobante; busqueda?: string; pagina?: number; desde?: string; hasta?: string } = {},
  ) => {
    const params = new URLSearchParams()
    if (filtros.tipo) params.set('tipo', filtros.tipo)
    if (filtros.busqueda) params.set('busqueda', filtros.busqueda)
    if (filtros.desde) params.set('desde', filtros.desde)
    if (filtros.hasta) params.set('hasta', filtros.hasta)
    params.set('pagina', String(filtros.pagina ?? 1))
    return peticion<PaginaDeCfdi>(`/api/clientes/${clienteId}/cfdi/?${params}`)
  },

  descargarCfdiSeleccionados: (clienteId: string, cfdiIds: string[]) =>
    descargarArchivo(`/api/clientes/${clienteId}/cfdi/descarga-masiva`, 'cfdi.zip', {
      method: 'POST',
      body: JSON.stringify({ cfdiIds }),
    }),

  /** Consulta el avance de una importación encolada (RNF-04: proceso asíncrono con avance visible). */
  obtenerImportacionCfdi: (clienteId: string, importacionId: string) =>
    peticion<ImportacionDeCfdi>(`/api/clientes/${clienteId}/cfdi/importaciones/${importacionId}`),

  /** Sube los archivos y los encola (202); el resultado llega por obtenerImportacionCfdi. */
  importarCfdi: async (clienteId: string, archivos: FileList | File[]): Promise<ImportacionDeCfdi> => {
    const token = await tokenDeSesion(obtenerToken)
    const cuerpo = new FormData()
    for (const archivo of Array.from(archivos)) cuerpo.append('archivos', archivo)

    const respuesta = await fetchDeApi(`${baseUrl}/api/clientes/${clienteId}/cfdi/importacion`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: cuerpo,
    })

    if (!respuesta.ok) {
      const cuerpoError = await respuesta.json().catch(() => ({}))
      throw new ErrorDeApi(respuesta.status, cuerpoError.error ?? `Error ${respuesta.status}`, cuerpoError.codigo)
    }

    return (await respuesta.json()) as ImportacionDeCfdi
  },

  obtenerXmlCfdi: async (clienteId: string, cfdiId: string): Promise<string> => {
    const token = await tokenDeSesion(obtenerToken)
    const respuesta = await fetchDeApi(`${baseUrl}/api/clientes/${clienteId}/cfdi/${cfdiId}/xml`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!respuesta.ok) throw new ErrorDeApi(respuesta.status, `Error ${respuesta.status}`)
    return respuesta.text()
  },

  // ---- Pólizas y periodos (RF-401..404) ----

  listarPeriodos: (clienteId: string, anio: number) =>
    peticion<ResumenPeriodo[]>(`/api/clientes/${clienteId}/periodos?anio=${anio}`),

  cerrarPeriodo: (clienteId: string, anio: number, mes: number) =>
    peticion<void>(`/api/clientes/${clienteId}/periodos/${anio}/${mes}/cierre`, { method: 'POST' }),

  reabrirPeriodo: (clienteId: string, anio: number, mes: number) =>
    peticion<void>(`/api/clientes/${clienteId}/periodos/${anio}/${mes}/reapertura`, { method: 'POST' }),

  listarPolizas: (clienteId: string, anio: number, mes: number) =>
    peticion<Poliza[]>(`/api/clientes/${clienteId}/polizas?anio=${anio}&mes=${mes}`),

  obtenerPoliza: (clienteId: string, polizaId: string) =>
    peticion<Poliza>(`/api/clientes/${clienteId}/polizas/${polizaId}`),

  registrarPoliza: (
    clienteId: string,
    poliza: {
      tipo: TipoPoliza
      fecha: string
      concepto: string
      movimientos: MovimientoPolizaRequest[]
      uuids?: string[]
    },
  ) => peticion<Poliza>(`/api/clientes/${clienteId}/polizas`, { method: 'POST', body: JSON.stringify(poliza) }),

  cancelarPoliza: (clienteId: string, polizaId: string, motivo: string) =>
    peticion<void>(`/api/clientes/${clienteId}/polizas/${polizaId}/cancelacion`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),

  corregirPoliza: (
    clienteId: string,
    polizaId: string,
    motivo: string,
    sustituta: {
      tipo: TipoPoliza
      fecha: string
      concepto: string
      movimientos: MovimientoPolizaRequest[]
      uuids?: string[]
    },
  ) =>
    peticion<Poliza>(`/api/clientes/${clienteId}/polizas/${polizaId}/correccion`, {
      method: 'POST',
      body: JSON.stringify({ motivo, sustituta }),
    }),

  vincularCfdi: (clienteId: string, polizaId: string, uuid: string, rfcEmisor?: string, montoTotal?: number) =>
    peticion<void>(`/api/clientes/${clienteId}/polizas/${polizaId}/cfdi`, {
      method: 'POST',
      body: JSON.stringify({ uuid, rfcEmisor, montoTotal }),
    }),

  desvincularCfdi: (clienteId: string, polizaId: string, uuid: string) =>
    peticion<void>(`/api/clientes/${clienteId}/polizas/${polizaId}/cfdi/${uuid}`, { method: 'DELETE' }),

  // ---- Plantillas de póliza recurrentes y generación programada (RF-405) ----

  listarPlantillas: (clienteId: string) =>
    peticion<PlantillaDePoliza[]>(`/api/clientes/${clienteId}/plantillas-poliza`),

  registrarPlantilla: (
    clienteId: string,
    plantilla: {
      tipo: TipoPoliza
      concepto: string
      periodicidad: PeriodicidadPlantilla
      renglones: RenglonDePlantillaRequest[]
    },
  ) => peticion<PlantillaDePoliza>(`/api/clientes/${clienteId}/plantillas-poliza`, { method: 'POST', body: JSON.stringify(plantilla) }),

  desactivarPlantilla: (clienteId: string, plantillaId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/plantillas-poliza/${plantillaId}/desactivacion`, { method: 'POST' }),

  reactivarPlantilla: (clienteId: string, plantillaId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/plantillas-poliza/${plantillaId}/reactivacion`, { method: 'POST' }),

  correrGeneracionDePlantillasAhora: (clienteId: string) =>
    peticion<{ creadas: number }>(`/api/clientes/${clienteId}/plantillas-poliza/generacion-ahora`, { method: 'POST' }),

  listarGeneracionesDePlantilla: (clienteId?: string, estado?: EstadoGeneracionPlantilla | '') => {
    const params = new URLSearchParams()
    if (clienteId) params.set('clienteId', clienteId)
    if (estado) params.set('estado', estado)
    const query = params.toString()
    return peticion<GeneracionDePlantilla[]>(`/api/plantillas-poliza/generaciones-pendientes${query ? `?${query}` : ''}`)
  },

  aprobarGeneracionDePlantilla: (generacionId: string) =>
    peticion<Poliza>(`/api/plantillas-poliza/generaciones-pendientes/${generacionId}/aprobacion`, { method: 'POST' }),

  rechazarGeneracionDePlantilla: (generacionId: string, motivo: string) =>
    peticion<void>(`/api/plantillas-poliza/generaciones-pendientes/${generacionId}/rechazo`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),

  // ---- Panel de contabilización (RF-601) ----

  panelResumen: (clienteId: string, anio: number, mes: number) =>
    peticion<ResumenDePanel>(`/api/clientes/${clienteId}/panel-contabilizacion/resumen?anio=${anio}&mes=${mes}`),

  panelListarCfdi: (clienteId: string, anio: number, mes: number, estado?: EstadoContabilizacionCfdi) => {
    const params = new URLSearchParams({ anio: String(anio), mes: String(mes) })
    if (estado) params.set('estado', estado)
    return peticion<CfdiDelPanel[]>(`/api/clientes/${clienteId}/panel-contabilizacion/cfdi?${params}`)
  },

  descartarCfdiDelPanel: (clienteId: string, cfdiId: string, motivo: string) =>
    peticion<void>(`/api/clientes/${clienteId}/panel-contabilizacion/cfdi/${cfdiId}/descarte`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),

  restaurarCfdiDelPanel: (clienteId: string, cfdiId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/panel-contabilizacion/cfdi/${cfdiId}/restauracion`, { method: 'POST' }),

  // ---- Reglas de contabilización y sugerencia (RF-602, RF-603) ----

  listarReglas: (clienteId: string) => peticion<ReglaDeContabilizacion[]>(`/api/clientes/${clienteId}/reglas-contabilizacion`),

  registrarRegla: (
    clienteId: string,
    regla: {
      rfcEmisor: string
      cuentaGastoId: string
      cuentaContrapartidaId: string
      cuentaIvaAcreditableId?: string
      cuentaIsrRetenidoId?: string
      cuentaIvaRetenidoId?: string
      descripcion?: string
    },
  ) => peticion<ReglaDeContabilizacion>(`/api/clientes/${clienteId}/reglas-contabilizacion`, {
    method: 'POST',
    body: JSON.stringify(regla),
  }),

  actualizarRegla: (
    clienteId: string,
    reglaId: string,
    regla: {
      rfcEmisor: string
      cuentaGastoId: string
      cuentaContrapartidaId: string
      cuentaIvaAcreditableId?: string
      cuentaIsrRetenidoId?: string
      cuentaIvaRetenidoId?: string
      descripcion?: string
    },
  ) => peticion<ReglaDeContabilizacion>(`/api/clientes/${clienteId}/reglas-contabilizacion/${reglaId}`, {
    method: 'PUT',
    body: JSON.stringify(regla),
  }),

  desactivarRegla: (clienteId: string, reglaId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/reglas-contabilizacion/${reglaId}/desactivacion`, { method: 'POST' }),

  reactivarRegla: (clienteId: string, reglaId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/reglas-contabilizacion/${reglaId}/reactivacion`, { method: 'POST' }),

  sugerirPoliza: (clienteId: string, cfdiId: string) =>
    peticion<PolizaSugerida>(`/api/clientes/${clienteId}/panel-contabilizacion/cfdi/${cfdiId}/sugerencia`),

  sugerirPolizaLote: (clienteId: string, cfdiIds: string[]) =>
    peticion<PolizaSugerida>(`/api/clientes/${clienteId}/panel-contabilizacion/sugerencia-lote`, {
      method: 'POST',
      body: JSON.stringify({ cfdiIds }),
    }),

  // ---- Reportes (RF-701, RF-702, RF-704) ----

  balanza: (clienteId: string, anio: number, mes: number) =>
    peticion<Balanza>(`/api/clientes/${clienteId}/reportes/balanza?anio=${anio}&mes=${mes}`),

  libroMayor: (clienteId: string, anio: number, mes: number, cuentaId: string) =>
    peticion<LibroMayor>(`/api/clientes/${clienteId}/reportes/libro-mayor?anio=${anio}&mes=${mes}&cuentaId=${cuentaId}`),

  libroDiario: (clienteId: string, anio: number, mes: number) =>
    peticion<Poliza[]>(`/api/clientes/${clienteId}/reportes/libro-diario?anio=${anio}&mes=${mes}`),

  balanceGeneral: (clienteId: string, anio: number, mes: number, anioComparativo?: number, mesComparativo?: number) =>
    peticion<BalanceGeneral>(
      `/api/clientes/${clienteId}/reportes/balance-general?anio=${anio}&mes=${mes}` +
        (anioComparativo && mesComparativo ? `&anioComparativo=${anioComparativo}&mesComparativo=${mesComparativo}` : ''),
    ),

  estadoDeResultados: (clienteId: string, anio: number, mes: number, anioComparativo?: number, mesComparativo?: number) =>
    peticion<EstadoDeResultados>(
      `/api/clientes/${clienteId}/reportes/estado-resultados?anio=${anio}&mes=${mes}` +
        (anioComparativo && mesComparativo ? `&anioComparativo=${anioComparativo}&mesComparativo=${mesComparativo}` : ''),
    ),

  congruenciaGlobal: (clienteId: string, anio: number, mes: number) =>
    peticion<CongruenciaGlobal>(`/api/clientes/${clienteId}/reportes/congruencia?anio=${anio}&mes=${mes}`),

  // ---- Papel de trabajo de IVA (RF-1203) ----
  borradorDePapelDeIva: (clienteId: string, anio: number, mes: number, saldoAFavorAplicado = 0, origenDelSaldoAFavor = '') =>
    peticion<PapelDeIva>(
      `/api/clientes/${clienteId}/iva/papel-de-trabajo/borrador?anio=${anio}&mes=${mes}` +
        (saldoAFavorAplicado > 0
          ? `&saldoAFavorAplicado=${saldoAFavorAplicado}&origenDelSaldoAFavor=${encodeURIComponent(origenDelSaldoAFavor)}`
          : ''),
    ),

  sellarPapelDeIva: (clienteId: string, solicitud: {
    anio: number; mes: number; saldoAFavorAplicado: number; origenDelSaldoAFavor?: string
  }) => peticion<PapelDeIva>(`/api/clientes/${clienteId}/iva/papeles-de-trabajo`, { method: 'POST', body: JSON.stringify(solicitud) }),

  papelesDeIva: (clienteId: string, anio: number, mes: number) =>
    peticion<PapelDeIvaResumen[]>(`/api/clientes/${clienteId}/iva/papeles-de-trabajo?anio=${anio}&mes=${mes}`),

  papelDeIva: (clienteId: string, papelId: string) =>
    peticion<PapelDeIva>(`/api/clientes/${clienteId}/iva/papeles-de-trabajo/${papelId}`),

  exportarPapelDeIva: (clienteId: string, papel: { id: string; anio: number; mes: number }, formato: 'excel' | 'pdf') =>
    descargarArchivo(
      `/api/clientes/${clienteId}/iva/papeles-de-trabajo/${papel.id}/exportar?formato=${formato}`,
      `papel-iva-${papel.anio}${String(papel.mes).padStart(2, '0')}.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    ),

  // ---- Parámetros de ISR del ejercicio, por tramos (RF-1208) ----
  /** Los tramos del ejercicio, del primero al último; vacío si nunca se capturó: no hay dato, que no es un cero. */
  parametrosDeIsr: (clienteId: string, ejercicio: number) =>
    peticion<ParametrosDeIsr[]>(`/api/clientes/${clienteId}/isr-moral/parametros/${ejercicio}`),

  capturarParametrosDeIsr: (clienteId: string, ejercicio: number, captura: CapturaDeParametrosDeIsr) =>
    peticion<ParametrosDeIsr>(`/api/clientes/${clienteId}/isr-moral/parametros/${ejercicio}`, {
      method: 'PUT', body: JSON.stringify(captura),
    }),

  // ---- Papel de trabajo del pago provisional de ISR de personas morales (RF-1206) ----
  borradorDePapelDeIsr: (clienteId: string, anio: number, mes: number, datos: DatosDelPapelDeIsr) => {
    const consulta = new URLSearchParams({ anio: String(anio), mes: String(mes) })
    for (const [campo, valor] of Object.entries(datos)) if (valor !== undefined) consulta.set(campo, String(valor))
    return peticion<PapelDeIsr>(`/api/clientes/${clienteId}/isr-moral/papel-de-trabajo/borrador?${consulta}`)
  },

  /** Sella contra la versión de los parámetros del borrador: si ya cambió, el servidor responde PARAMETROS_CAMBIARON. */
  sellarPapelDeIsr: (
    clienteId: string,
    solicitud: DatosDelPapelDeIsr & { anio: number; mes: number; capturaDeParametrosId: string | null },
  ) =>
    peticion<PapelDeIsr>(`/api/clientes/${clienteId}/isr-moral/papeles-de-trabajo`, { method: 'POST', body: JSON.stringify(solicitud) }),

  papelesDeIsr: (clienteId: string, anio: number, mes: number) =>
    peticion<PapelDeIsrResumen[]>(`/api/clientes/${clienteId}/isr-moral/papeles-de-trabajo?anio=${anio}&mes=${mes}`),

  papelDeIsr: (clienteId: string, papelId: string) =>
    peticion<PapelDeIsr>(`/api/clientes/${clienteId}/isr-moral/papeles-de-trabajo/${papelId}`),

  exportarPapelDeIsr: (clienteId: string, papel: { id: string; anio: number; mes: number }, formato: 'excel' | 'pdf') =>
    descargarArchivo(
      `/api/clientes/${clienteId}/isr-moral/papeles-de-trabajo/${papel.id}/exportar?formato=${formato}`,
      `papel-isr-${papel.anio}${String(papel.mes).padStart(2, '0')}.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    ),

  // ---- Conciliación CFDI ↔ contabilidad (RF-1205) ----
  cedulaDeConciliacionCfdi: (clienteId: string, anio: number, mes: number) =>
    peticion<CedulaDeConciliacion>(`/api/clientes/${clienteId}/conciliacion-cfdi/cedula?anio=${anio}&mes=${mes}`),

  exportarConciliacionCfdi: (clienteId: string, anio: number, mes: number, formato: 'excel' | 'pdf') =>
    descargarArchivo(
      `/api/clientes/${clienteId}/conciliacion-cfdi/cedula/exportar?anio=${anio}&mes=${mes}&formato=${formato}`,
      `conciliacion-cfdi-${anio}${String(mes).padStart(2, '0')}.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    ),

  // ---- Series y folios (RF-1005) ----
  seriesDeFolios: (clienteId: string) => peticion<SerieDeFolios[]>(`/api/clientes/${clienteId}/series-de-folios`),

  crearSerieDeFolios: (clienteId: string, solicitud: { serie: string; descripcion?: string; folioInicial?: number }) =>
    peticion<SerieDeFolios>(`/api/clientes/${clienteId}/series-de-folios`, { method: 'POST', body: JSON.stringify(solicitud) }),

  desactivarSerieDeFolios: (clienteId: string, serieId: string) =>
    peticion<SerieDeFolios>(`/api/clientes/${clienteId}/series-de-folios/${serieId}/desactivacion`, { method: 'POST' }),

  reactivarSerieDeFolios: (clienteId: string, serieId: string) =>
    peticion<SerieDeFolios>(`/api/clientes/${clienteId}/series-de-folios/${serieId}/reactivacion`, { method: 'POST' }),

  foliosApartados: (clienteId: string, estado?: EstadoDeFolio) =>
    peticion<FolioApartado[]>(`/api/clientes/${clienteId}/folios${estado ? `?estado=${estado}` : ''}`),

  descartarFolio: (clienteId: string, folioId: string, motivo: string) =>
    peticion<FolioApartado>(`/api/clientes/${clienteId}/folios/${folioId}/descarte`, {
      method: 'POST', body: JSON.stringify({ motivo }),
    }),

  exportarBalanza: (clienteId: string, anio: number, mes: number, formato: 'excel' | 'pdf') =>
    descargarArchivo(
      `/api/clientes/${clienteId}/reportes/balanza/exportar?anio=${anio}&mes=${mes}&formato=${formato}`,
      `balanza.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    ),

  exportarLibroMayor: (clienteId: string, anio: number, mes: number, cuentaId: string, formato: 'excel' | 'pdf') =>
    descargarArchivo(
      `/api/clientes/${clienteId}/reportes/libro-mayor/exportar?anio=${anio}&mes=${mes}&cuentaId=${cuentaId}&formato=${formato}`,
      `mayor.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    ),

  exportarLibroDiario: (clienteId: string, anio: number, mes: number, formato: 'excel' | 'pdf') =>
    descargarArchivo(
      `/api/clientes/${clienteId}/reportes/libro-diario/exportar?anio=${anio}&mes=${mes}&formato=${formato}`,
      `diario.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    ),

  exportarBalanceGeneral: (clienteId: string, anio: number, mes: number, formato: 'excel' | 'pdf', anioComparativo?: number, mesComparativo?: number) =>
    descargarArchivo(
      `/api/clientes/${clienteId}/reportes/balance-general/exportar?anio=${anio}&mes=${mes}&formato=${formato}` +
        (anioComparativo && mesComparativo ? `&anioComparativo=${anioComparativo}&mesComparativo=${mesComparativo}` : ''),
      `balance.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    ),

  exportarEstadoDeResultados: (clienteId: string, anio: number, mes: number, formato: 'excel' | 'pdf', anioComparativo?: number, mesComparativo?: number) =>
    descargarArchivo(
      `/api/clientes/${clienteId}/reportes/estado-resultados/exportar?anio=${anio}&mes=${mes}&formato=${formato}` +
        (anioComparativo && mesComparativo ? `&anioComparativo=${anioComparativo}&mesComparativo=${mesComparativo}` : ''),
      `resultados.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    ),

  /** Paquete de cierre mensual: balanza, balance general, estado de resultados y XML del periodo en un ZIP (RF-903). */
  descargarPaqueteDeCierre: (clienteId: string, anio: number, mes: number) =>
    descargarArchivo(`/api/clientes/${clienteId}/reportes/paquete-de-cierre?anio=${anio}&mes=${mes}`, 'cierre.zip'),

  /**
   * Paquete mensual de declaraciones: papeles de IVA e ISR vigentes y cédula de conciliación en un ZIP, con un
   * LEEME de lo que trae y lo que falta (RF-1210). Aparte del paquete de cierre.
   */
  descargarPaqueteDeDeclaraciones: (clienteId: string, anio: number, mes: number) =>
    descargarArchivo(`/api/clientes/${clienteId}/declaraciones/paquete?anio=${anio}&mes=${mes}`, 'declaraciones.zip'),

  // ---- RF-1607 · RF-1608 · Lo que falta presentar del periodo ----

  /**
   * RF-1607 · Todo lo que el cliente tiene pendiente de presentar en el periodo, en un ZIP: los archivos del
   * Anexo 24 sin enviar o rechazados, los papeles de las declaraciones sin presentar y un LEEME con la guía.
   */
  descargarPaqueteDePresentacion: (clienteId: string, anio: number, mes: number) =>
    descargarArchivo(`/api/clientes/${clienteId}/presentacion/${anio}/${mes}/paquete`, 'presentacion.zip'),

  /** RF-1608 · Qué le falta presentar a cada cliente de la cartera en el periodo. */
  pendientesDePresentacion: (anio: number, mes: number) =>
    peticion<PendientesDePresentacion>(`/api/presentacion/pendientes?anio=${anio}&mes=${mes}`),

  // ---- RF-1605 · Control de presentación de declaraciones ----

  /** IVA, ISR y DIOT del periodo: en qué etapa va cada una y sus presentaciones registradas. */
  obtenerSeguimientoDeDeclaraciones: (clienteId: string, anio: number, mes: number) =>
    peticion<SeguimientoDeDeclaraciones>(`/api/clientes/${clienteId}/declaraciones/${anio}/${mes}`),

  /** Registra lo que el contador ya presentó en el portal del SAT. No presenta nada (RN-62). */
  registrarPresentacionDeDeclaracion: (clienteId: string, datos: RegistrarPresentacionRequest) =>
    peticion<PresentacionDeDeclaracion>(`/api/clientes/${clienteId}/declaraciones/presentaciones`, {
      method: 'POST',
      body: JSON.stringify(datos),
    }),

  /** RN-61 · El acuse se resguarda una sola vez y nunca se reemplaza. PDF o ZIP. */
  resguardarAcuseDeDeclaracion: (clienteId: string, presentacionId: string, archivo: File) => {
    const cuerpo = new FormData()
    cuerpo.append('archivo', archivo)
    return peticion<PresentacionDeDeclaracion>(
      `/api/clientes/${clienteId}/declaraciones/presentaciones/${presentacionId}/acuse`,
      { method: 'POST', body: cuerpo },
    )
  },

  descargarAcuseDeDeclaracion: (clienteId: string, presentacionId: string) =>
    descargarArchivo(`/api/clientes/${clienteId}/declaraciones/presentaciones/${presentacionId}/acuse`, 'acuse-declaracion.pdf'),

  /** Solo el administrador, con motivo. La anulada se conserva como rastro. */
  anularPresentacionDeDeclaracion: (clienteId: string, presentacionId: string, motivo: string) =>
    peticion<PresentacionDeDeclaracion>(`/api/clientes/${clienteId}/declaraciones/presentaciones/${presentacionId}/anulacion`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),

  // ---- Balanza ajustada de cierre de ejercicio (periodo 13, RF-802/RF-806) ----

  /** Diciembre + los ajustes del periodo 13 capturados para el ejercicio; sin ajustes, coincide con diciembre. */
  balanzaAjustada: (clienteId: string, anioDelEjercicio: number) =>
    peticion<Balanza>(`/api/clientes/${clienteId}/balanza-ajustada/${anioDelEjercicio}`),

  listarAjustesDeCierre: (clienteId: string, anioDelEjercicio: number) =>
    peticion<Poliza[]>(`/api/clientes/${clienteId}/balanza-ajustada/${anioDelEjercicio}/ajustes`),

  registrarAjusteDeCierre: (
    clienteId: string,
    anioDelEjercicio: number,
    ajuste: { concepto: string; movimientos: MovimientoDeAjusteRequest[] },
  ) =>
    peticion<Poliza>(`/api/clientes/${clienteId}/balanza-ajustada/${anioDelEjercicio}/ajustes`, {
      method: 'POST',
      body: JSON.stringify(ajuste),
    }),

  cancelarAjusteDeCierre: (clienteId: string, anioDelEjercicio: number, polizaId: string, motivo: string) =>
    peticion<void>(`/api/clientes/${clienteId}/balanza-ajustada/${anioDelEjercicio}/ajustes/${polizaId}/cancelacion`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),

  limiteBalanzaAjustada: (clienteId: string, anioDelEjercicio: number, tipoPersona: TipoPersona) =>
    peticion<{ fechaLimite: string }>(
      `/api/clientes/${clienteId}/balanza-ajustada/${anioDelEjercicio}/limite?tipoPersona=${tipoPersona}`,
    ),

  /**
   * El repositorio de CFDI de toda la cartera en una sola tabla (F6). Es de solo lectura:
   * importar, descargar el lote y verificar estatus siguen siendo acciones de un cliente.
   */
  cfdiDelDespacho: (filtros: {
    tipo?: TipoComprobante
    busqueda?: string
    pagina?: number
    desde?: string
    hasta?: string
  } = {}) => {
    const p = new URLSearchParams()
    for (const [clave, valor] of Object.entries(filtros)) {
      if (valor !== undefined && valor !== '') p.set(clave, String(valor))
    }
    const cadena = p.toString()
    return peticion<PaginaDeCfdi>(`/api/cfdi${cadena ? `?${cadena}` : ''}`)
  },

  /** Los envíos de toda la cartera por periodo: el cierre mensual pregunta «¿quién me falta?». */
  controlDeEnviosDelDespacho: (anio?: number, mes?: number) => {
    const p = new URLSearchParams()
    if (anio !== undefined) p.set('anio', String(anio))
    if (mes !== undefined) p.set('mes', String(mes))
    const cadena = p.toString()
    return peticion<ControlDeEnvioDelDespacho[]>(`/api/control-envios${cadena ? `?${cadena}` : ''}`)
  },

  /**
   * Los plazos legales del periodo, por tipo de persona (A2, RF-806). En la pantalla de un
   * cliente el tipo de persona es uno solo; en el cierre del despacho conviven morales y
   * físicas, que vencen en días distintos para el MISMO periodo.
   */
  plazosDelPeriodo: (anio: number, mes: number) =>
    peticion<PlazosDelPeriodo>(`/api/control-envios/plazos?anio=${anio}&mes=${mes}`),

  resumenDeDescargaNocturna: (fecha?: string) =>
    peticion<ResumenDeDescargaNocturna>(
      `/api/despachos/actual/descarga-nocturna${fecha ? `?fecha=${fecha}` : ''}`,
    ),

  listarUsuarios: () => peticion<Usuario[]>('/api/usuarios'),

  invitarUsuario: (invitacion: { nombre: string; correo: string; rol: string }) =>
    peticion<ResultadoDeInvitacion & { usuarioId: string }>('/api/usuarios/invitaciones', {
      method: 'POST',
      body: JSON.stringify(invitacion),
    }),

  cambiarRolUsuario: (usuarioId: string, rol: string) =>
    peticion<void>(`/api/usuarios/${usuarioId}/rol?rol=${rol}`, { method: 'PUT' }),

  /**
   * U3 · Otra entrega de la invitación a quien todavía no ha activado su acceso. POST a un
   * sub-recurso sustantivo: crea una invitación más, no ejecuta un verbo.
   */
  reenviarInvitacion: (usuarioId: string) =>
    peticion<ResultadoDeInvitacion>(`/api/usuarios/${usuarioId}/invitaciones`, { method: 'POST' }),

  /** RF-105 · Cierra las demás sesiones del usuario en curso; exige reautenticarse. */
  cerrarOtrasSesiones: () =>
    peticion<void>('/api/usuarios/mi-sesion/cerrar-otras', { method: 'POST' }),

  // ---- Ayuda en la plataforma (RF-1700) ----

  /** RF-1707 · Qué recorridos, TIPs y novedades ya vio u omitió el usuario en curso. */
  obtenerMiAyuda: () => peticion<EstadoDeAyuda>('/api/usuarios/mi-ayuda'),

  /** RF-1707 · Registra un recorrido (con su versión), TIP o novedad visto u omitido, o el cierre del TIP del día. */
  marcarAyudaVista: (vista: { tipo: TipoDeAyuda; clave?: string; version?: number; dia?: string }) =>
    peticion<EstadoDeAyuda>('/api/usuarios/mi-ayuda/vistos', { method: 'POST', body: JSON.stringify(vista) }),

  /** RF-1707 · Enciende o apaga los TIPs; la ayuda junto al campo sigue disponible porque solo sale al pedirla. */
  cambiarMostrarTips: (mostrarTips: boolean) =>
    peticion<EstadoDeAyuda>('/api/usuarios/mi-ayuda/preferencias', { method: 'POST', body: JSON.stringify({ mostrarTips }) }),

  // ---- Consentimientos de soporte de plataforma (RF-107, §6.4) ----

  listarConsentimientosDeSoporte: () =>
    peticion<ConsentimientoDeSoporte[]>('/api/despachos/actual/consentimientos-de-soporte/'),

  otorgarConsentimientoDeSoporte: (datos: { proposito: string; diasDeVigencia: number }) =>
    peticion<ConsentimientoDeSoporte>('/api/despachos/actual/consentimientos-de-soporte/', {
      method: 'POST',
      body: JSON.stringify(datos),
    }),

  revocarConsentimientoDeSoporte: (id: string) =>
    peticion<void>(`/api/despachos/actual/consentimientos-de-soporte/${id}/revocacion`, { method: 'POST' }),

  revocarUsuario: (usuarioId: string) =>
    peticion<void>(`/api/usuarios/${usuarioId}`, { method: 'DELETE' }),

  // ---- Cartera de clientes asignada (RF-104, cédula I-02, A-05) ----

  clientesAsignados: (usuarioId: string) => peticion<string[]>(`/api/usuarios/${usuarioId}/clientes-asignados`),

  /** La relación al revés: los colaboradores que tienen a este cliente en su cartera (E7). */
  usuariosAsignados: (clienteId: string) =>
    peticion<UsuarioAsignado[]>(`/api/clientes/${clienteId}/usuarios-asignados`),

  asignarClientes: (usuarioId: string, clienteIds: string[]) =>
    peticion<void>(`/api/usuarios/${usuarioId}/clientes-asignados`, {
      method: 'PUT',
      body: JSON.stringify({ clienteIds }),
    }),

  // ---- Bitácora de auditoría (RF-106, RNF-07) ----

  bitacora: (filtros: {
    desdeUtc?: string
    hastaUtc?: string
    entidad?: string
    usuarioId?: string
    pagina?: number
    tamano?: number
  }) => {
    const params = new URLSearchParams()
    if (filtros.desdeUtc) params.set('desdeUtc', filtros.desdeUtc)
    if (filtros.hastaUtc) params.set('hastaUtc', filtros.hastaUtc)
    if (filtros.entidad) params.set('entidad', filtros.entidad)
    if (filtros.usuarioId) params.set('usuarioId', filtros.usuarioId)
    params.set('pagina', String(filtros.pagina ?? 1))
    params.set('tamano', String(filtros.tamano ?? 100))
    return peticion<{ total: number; pagina: number; items: RegistroBitacora[] }>(`/api/bitacora/?${params}`)
  },

  exportarBitacora: (filtros: { desdeUtc?: string; hastaUtc?: string; entidad?: string; usuarioId?: string }) => {
    const params = new URLSearchParams()
    if (filtros.desdeUtc) params.set('desdeUtc', filtros.desdeUtc)
    if (filtros.hastaUtc) params.set('hastaUtc', filtros.hastaUtc)
    if (filtros.entidad) params.set('entidad', filtros.entidad)
    if (filtros.usuarioId) params.set('usuarioId', filtros.usuarioId)
    return descargarArchivo(`/api/bitacora/exportacion?${params}`, 'bitacora.csv')
  },

  // ---- Conciliación bancaria (RF-1300 a RF-1303) ----

  bancosSat: () => peticion<BancoSat[]>('/api/catalogos/bancos'),

  cuentasBancarias: (clienteId: string) => peticion<CuentaBancaria[]>(`/api/clientes/${clienteId}/cuentas-bancarias`),

  registrarCuentaBancaria: (clienteId: string, alta: AltaDeCuentaBancaria) =>
    peticion<CuentaBancaria>(`/api/clientes/${clienteId}/cuentas-bancarias`, { method: 'POST', body: JSON.stringify(alta) }),

  desactivarCuentaBancaria: (clienteId: string, cuentaId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/desactivacion`, { method: 'POST' }),

  reactivarCuentaBancaria: (clienteId: string, cuentaId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/reactivacion`, { method: 'POST' }),

  perfilesBancarios: () => peticion<PerfilBancario[]>('/api/perfiles-bancarios'),

  crearPerfilBancario: (datos: DatosDePerfilBancario) =>
    peticion<PerfilBancario>('/api/perfiles-bancarios', { method: 'POST', body: JSON.stringify(datos) }),

  actualizarPerfilBancario: (perfilId: string, datos: DatosDePerfilBancario) =>
    peticion<PerfilBancario>(`/api/perfiles-bancarios/${perfilId}`, { method: 'PUT', body: JSON.stringify(datos) }),

  /** RF-1302: lee el archivo con el perfil y dice qué entraría, sin escribir nada. */
  vistaPreviaDeEstadoDeCuenta: (clienteId: string, cuentaId: string, perfilId: string, archivo: File) =>
    peticion<ResultadoDeEstadoDeCuenta>(`/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/vista-previa`, {
      method: 'POST',
      body: formularioDeEstadoDeCuenta(perfilId, archivo),
    }),

  /** RF-1302: incorpora los renglones nuevos del MISMO archivo que se previsualizó. */
  importarEstadoDeCuenta: (clienteId: string, cuentaId: string, perfilId: string, archivo: File) =>
    peticion<ResultadoDeEstadoDeCuenta>(`/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/importaciones`, {
      method: 'POST',
      body: formularioDeEstadoDeCuenta(perfilId, archivo),
    }),

  importacionesBancarias: (clienteId: string, cuentaId: string) =>
    peticion<ImportacionBancaria[]>(`/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/importaciones`),

  movimientosBancarios: (clienteId: string, cuentaId: string, filtros: PeriodoBancario = {}) =>
    peticion<MovimientoBancario[]>(`/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/movimientos${consultaDePeriodo(filtros)}`),

  // ---- Conciliación bancaria · emparejamiento (RF-1304, RF-1305, RF-1307) ----

  /** Lo que falta emparejar en el periodo: movimientos bancarios, de póliza y pagos de CFDI. */
  partidasPendientesBancarias: (clienteId: string, cuentaId: string, periodo: PeriodoBancario = {}) =>
    peticion<PartidasPendientes>(
      `/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/partidas-pendientes${consultaDePeriodo(periodo)}`,
    ),

  /** RF-1304 · RF-1307: lo que propone el motor. No concilia nada por sí mismo (RN-36). */
  propuestasDeEmparejamiento: (clienteId: string, cuentaId: string, periodo: PeriodoBancario = {}) =>
    peticion<PropuestaDeEmparejamiento[]>(
      `/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/propuestas${consultaDePeriodo(periodo)}`,
    ),

  emparejamientosBancarios: (
    clienteId: string, cuentaId: string, filtros: PeriodoBancario & { incluirDeshechos?: boolean } = {},
  ) =>
    peticion<EmparejamientoBancario[]>(
      `/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/emparejamientos${consultaDePeriodo(filtros, filtros.incluirDeshechos)}`,
    ),

  /** Todo o nada: si una partida no cuadra o ya estaba emparejada, no se guarda ninguna. */
  emparejar: (clienteId: string, cuentaId: string, partidas: PartidaDeEmparejamiento[]) =>
    peticion<EmparejamientoBancario[]>(`/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/emparejamientos`, {
      method: 'POST',
      body: JSON.stringify({ partidas }),
    }),

  /** RF-1305: deshace un emparejamiento; sus movimientos vuelven a pendientes y queda el rastro. */
  deshacerEmparejamiento: (clienteId: string, cuentaId: string, emparejamientoId: string) =>
    peticion<void>(`/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/emparejamientos/${emparejamientoId}/reversa`, {
      method: 'POST',
    }),

  // ---- Conciliación bancaria · el documento con cierre (RF-1308) y la antigüedad (RF-1309) ----

  /** El documento del mes: abierto, calculado al momento; cerrado, la foto del cierre (RN-38). */
  conciliacionBancaria: (clienteId: string, cuentaId: string, anio: number, mes: number) =>
    peticion<ConciliacionDelPeriodo>(rutaDeConciliacion(clienteId, cuentaId, anio, mes)),

  /** RN-39: el servidor no cierra con diferencia sin explicar. Cierran el administrador y el contador. */
  cerrarConciliacionBancaria: (clienteId: string, cuentaId: string, anio: number, mes: number) =>
    peticion<ConciliacionDelPeriodo>(`${rutaDeConciliacion(clienteId, cuentaId, anio, mes)}/cierre`, { method: 'POST' }),

  /** RN-38: solo el administrador reabre, con motivo, y queda en bitácora. */
  reabrirConciliacionBancaria: (clienteId: string, cuentaId: string, anio: number, mes: number, motivo: string) =>
    peticion<ConciliacionDelPeriodo>(`${rutaDeConciliacion(clienteId, cuentaId, anio, mes)}/reapertura`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),

  /** RF-1308: el documento en Excel o PDF con el encabezado del cliente. */
  exportarConciliacionBancaria: (clienteId: string, cuentaId: string, anio: number, mes: number, formato: 'excel' | 'pdf') =>
    descargarArchivo(
      `${rutaDeConciliacion(clienteId, cuentaId, anio, mes)}/exportacion?formato=${formato}`,
      `conciliacion-bancaria-${anio}-${String(mes).padStart(2, '0')}.${formato === 'excel' ? 'xlsx' : 'pdf'}`,
    ),

  /** RF-1309: sin corte, el servidor usa hoy. */
  partidasEnConciliacion: (clienteId: string, cuentaId: string, corte?: string) =>
    peticion<PartidasEnConciliacion>(
      `/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/partidas-en-conciliacion${corte ? `?corte=${encodeURIComponent(corte)}` : ''}`,
    ),
}

function rutaDeConciliacion(clienteId: string, cuentaId: string, anio: number, mes: number): string {
  return `/api/clientes/${clienteId}/cuentas-bancarias/${cuentaId}/conciliaciones/${anio}/${mes}`
}

/** «?desde=…&hasta=…» con solo lo que venga; vacío si no viene nada. */
function consultaDePeriodo(periodo: PeriodoBancario, incluirDeshechos?: boolean): string {
  const params = new URLSearchParams()
  if (periodo.desde) params.set('desde', periodo.desde)
  if (periodo.hasta) params.set('hasta', periodo.hasta)
  if (incluirDeshechos) params.set('incluirDeshechos', 'true')
  const consulta = params.toString()
  return consulta ? `?${consulta}` : ''
}

/** El multipart de la vista previa y de la importación: el archivo y el perfil con que se lee. */
function formularioDeEstadoDeCuenta(perfilId: string, archivo: File): FormData {
  const cuerpo = new FormData()
  cuerpo.append('archivo', archivo)
  cuerpo.append('perfilId', perfilId)
  return cuerpo
}
